/* ============================================================================
   Aroma 247 — Scent Configurator
   Vanilla ES6. No dependencies. One state object, one render pass per change.
   ========================================================================== */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     MOBILE NAV FIX — Continue / Back buttons
     This file only contains behaviour; the section's markup and stylesheet
     live outside it. Rather than guess at unseen CSS, this injects a small,
     self-contained mobile-only override:
       - The space/area summary toggle ([data-action="toggle-summary"]) and
         Continue/See pricing (.aroma-sticky__cta / [data-action="next"])
         originally shared one bar (.aroma-sticky) — an earlier version of
         this fix pulled Continue out into its own floating pill, which
         visually separated it from the summary box. This restores them to
         ONE fixed, rounded container, raised clear of the theme's own
         bottom tab bar and chat bubble.
       - Back ([data-action="prev"]) stays pinned on its own on the opposite
         (left) edge, since it isn't part of that shared bar and is hidden
         on step 1 anyway.
       - Neutralises the is-keyboard-open / is-scrolled-out hide states so
         nothing disappears while typing, selecting, or scrolling.
     Only applies at <=749px; desktop layout is untouched.

     --aroma-mobile-nav-bottom is the single value to tweak if the bar still
     overlaps the site's tab bar / chat bubble on a given device — raise it
     (larger px) to lift the bar higher, lower it to drop it down.
     -------------------------------------------------------------------------- */
  const injectMobileNavStyles = () => {
    if (document.getElementById('aroma-mobile-nav-fix')) return;
    const style = document.createElement('style');
    style.id = 'aroma-mobile-nav-fix';
    style.textContent = [
      '@media (max-width: 749px) {',
      '  [data-aroma-root] {',
      '    --aroma-mobile-nav-bottom: 78px;',
      '  }',
      '  [data-aroma-root] .aroma-sticky {',
      '    pointer-events: auto !important;',
      '    position: fixed !important;',
      '    top: auto !important;',
      '    bottom: var(--aroma-mobile-nav-bottom) !important;',
      '    left: 14px !important;',
      '    right: auto !important;',
      '    width: fit-content !important;',
      '    display: flex !important;',
      '    align-items: center !important;',
      '    justify-content: space-between !important;',
      '    gap: 12px !important;',
      '    margin: 0 !important;',
      '    padding: 8px 8px 8px 18px !important;',
      '    background: #fff !important;',
      '    border-radius: 20px !important;',
      '    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22) !important;',
      '    z-index: 999 !important;',
      '    transform: none !important;',
      '    opacity: 1 !important;',
      '    visibility: visible !important;',
      '  }',
      '  [data-aroma-root] .aroma-sticky.is-keyboard-open,',
      '  [data-aroma-root] .aroma-sticky.is-scrolled-out {',
      '    transform: none !important;',
      '    opacity: 1 !important;',
      '    visibility: visible !important;',
      '    display: flex !important;',
      '  }',
      '  [data-aroma-root] .aroma-sticky [data-action="toggle-summary"],',
      '  [data-aroma-root] .aroma-sticky [data-summary-mobile="line"] {',
      '    pointer-events: auto !important;',
      '    background: none !important;',
      '    box-shadow: none !important;',
      '    border: none !important;',
      '    padding: 0 !important;',
      '    margin: 0 !important;',
      '    font-size: 13px !important;',
      '    font-weight: 600 !important;',
      '    white-space: nowrap !important;',
      '    overflow: hidden !important;',
      '    text-overflow: ellipsis !important;',
      '    flex: 1 1 auto !important;',
      '    min-width: 0 !important;',
      '  }',
      '  [data-aroma-root] .aroma-sticky__cta,',
      '  [data-aroma-root] .aroma-sticky [data-action="next"] {',
      '    pointer-events: auto !important;',
      '    position: static !important;',
      '    margin: 0 !important;',
      '    flex: 0 0 auto !important;',
      '    width: auto !important;',
      '    min-width: 96px !important;',
      '    min-height: 44px !important;',
      '    padding: 11px 20px !important;',
      '    font-size: 14px !important;',
      '    font-weight: 600 !important;',
      '    line-height: 1.2 !important;',
      '    border-radius: 999px !important;',
      '    white-space: nowrap !important;',
      '    box-shadow: none !important;',
      '    opacity: 1 !important;',
      '    visibility: visible !important;',
      '  }',
      '  [data-aroma-root] [data-action="prev"] {',
      '    pointer-events: auto !important;',
      '    position: fixed !important;',
      '    top: auto !important;',
      '    bottom: var(--aroma-mobile-nav-bottom) !important;',
      '    left: 14px !important;',
      '    right: auto !important;',
      '    transform: none !important;',
      '    margin: 0 !important;',
      '    z-index: 999 !important;',
      '    width: auto !important;',
      '    min-width: 96px !important;',
      '    min-height: 48px !important;',
      '    padding: 13px 18px !important;',
      '    font-size: 15px !important;',
      '    font-weight: 600 !important;',
      '    line-height: 1.2 !important;',
      '    border-radius: 999px !important;',
      '    white-space: nowrap !important;',
      '    box-shadow: 0 3px 14px rgba(0, 0, 0, 0.28) !important;',
      '    opacity: 1 !important;',
      '    visibility: visible !important;',
      '  }',
      '  [data-aroma-root] [data-action="prev"][hidden] {',
      '    display: none !important;',
      '  }',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  };


  /* --------------------------------------------------------------------------
     CATALOGUE
     The real catalogue is built in the section Liquid from live Shopify
     products, so prices and stock are always current. Edit the list at the
     top of sections/aroma-247-configurator.liquid to add or remove units.
     The entries below only appear if that list resolves to nothing, which
     means every handle in it is wrong or every product is unpublished.

     Even so, these are no longer generic guesses — they mirror the real
     Aroma 247 lineup (coverage + oil consumption as published on each
     product page) so that if the live feed ever fails, customers still see
     accurate figures instead of made-up ones. They are still deliberately
     kept unpurchasable (variantId: null, price: 0) so nothing gets sold off
     a stale fallback — the plan table already flags any diffuser with no
     linked variant as "Not linked to a product". mlPerHourMin/mlPerHourMax
     are the real published range for that product; where its page only
     gives one number, both are set equal to it (no range, no average).
     -------------------------------------------------------------------------- */
  const PRODUCT_MAP = { membership: 'aroma-247-membership' };

  const FALLBACK_DIFFUSERS = [
    { id: 'fallback-sensei',        title: 'SENSEI Plug-in Room Diffuser',            coverageMax: 250,   price: 0, fit: 'std',  mlPerHourMin: 0.08, mlPerHourMax: 0.08, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/sensei-plug-in-room-diffuser' },
    { id: 'fallback-airmax-novo',   title: 'AIRMAX NOVO Scent Diffuser',              coverageMax: 200,   price: 0, fit: 'std',  mlPerHourMin: 0.05, mlPerHourMax: 0.15, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/airmax-novo-scent-diffuser' },
    { id: 'fallback-airmax-100',    title: 'AIRMAX 100 Scent Diffuser',               coverageMax: 400,   price: 0, fit: 'std',  mlPerHourMin: 0.08, mlPerHourMax: 0.60, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/airmax-100-scent-diffuser' },
    { id: 'fallback-scent-splash',  title: 'SCENT SPLASH Scent Diffuser Machine',     coverageMax: 750,   price: 0, fit: 'std',  mlPerHourMin: 0.20, mlPerHourMax: 1.00, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/scent-splash-scent-diffuser-machine-1' },
    { id: 'fallback-scentpro',      title: 'SCENTPRO Scent Diffuser',                 coverageMax: 1600,  price: 0, fit: 'std',  mlPerHourMin: 0.50, mlPerHourMax: 0.50, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/scentpro-scent-diffuser' },
    { id: 'fallback-ecoscent-mini', title: 'ECOSCENT Mini Smart Scent Diffuser',      coverageMax: 1250,  price: 0, fit: 'std',  mlPerHourMin: 1.10, mlPerHourMax: 1.10, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/ecoscent-smart-scent-diffuser' },
    { id: 'fallback-airslim-500',   title: 'AIRSLIM 500 Aroma Diffuser',              coverageMax: 2000,  price: 0, fit: 'std',  mlPerHourMin: 0.50, mlPerHourMax: 0.50, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/airslim-500-aroma-diffusers' },
    { id: 'fallback-ecoscent-max',  title: 'ECOSCENT Max Smart Scent Diffuser',       coverageMax: 2000,  price: 0, fit: 'std',  mlPerHourMin: 1.50, mlPerHourMax: 1.50, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/ecoscent-smart-scent-diffuser' },
    { id: 'fallback-airscent-1000', title: 'AIRSCENT 1000 HVAC Scent Diffuser',       coverageMax: 2000,  price: 0, fit: 'hvac', mlPerHourMin: 1.50, mlPerHourMax: 1.50, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/airscent-hvac-scent-diffuser' },
    { id: 'fallback-scent-trio',    title: 'SCENT TRIO Smart Aroma Diffuser',         coverageMax: 3000,  price: 0, fit: 'std',  mlPerHourMin: 0.50, mlPerHourMax: 1.00, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/scent-trio-smart-aroma-diffusers-for-home' },
    { id: 'fallback-scentbox-1800', title: 'SCENT BOX 1800F Scent Diffuser Machine',  coverageMax: 3600,  price: 0, fit: 'std',  mlPerHourMin: 1.50, mlPerHourMax: 1.50, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/scent-box-scent-diffuser-machine' },
    { id: 'fallback-ecoscent-ultra',title: 'ECOSCENT Ultra Smart Scent Diffuser',     coverageMax: 4000,  price: 0, fit: 'std',  mlPerHourMin: 2.25, mlPerHourMax: 2.25, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/ecoscent-smart-scent-diffuser' },
    { id: 'fallback-scentbox-3000', title: 'SCENT BOX 3000F Scent Diffuser Machine',  coverageMax: 6000,  price: 0, fit: 'std',  mlPerHourMin: 2.50, mlPerHourMax: 2.50, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/scent-box-scent-diffuser-machine' },
    { id: 'fallback-airscent-3000', title: 'AIRSCENT 3000 HVAC Scent Diffuser',       coverageMax: 6000,  price: 0, fit: 'hvac', mlPerHourMin: 2.00, mlPerHourMax: 2.00, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/airscent-hvac-scent-diffuser' },
    { id: 'fallback-airscent-5000', title: 'AIRSCENT 5000 HVAC Scent Diffuser',       coverageMax: 10000, price: 0, fit: 'hvac', mlPerHourMin: 4.00, mlPerHourMax: 4.00, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/airscent-hvac-scent-diffuser' },
    { id: 'fallback-airscent-10000',title: 'AIRSCENT 10000 HVAC Scent Diffuser',      coverageMax: 15000, price: 0, fit: 'hvac', mlPerHourMin: 8.00, mlPerHourMax: 8.00, variantId: null, available: true, productUrl: 'https://aroma-247.com.pk/products/airscent-hvac-scent-diffuser' }
  ];

  /* --------------------------------------------------------------------------
     PROPERTY DEFINITIONS
     `mode: 'unit'`     → one card per space (Bedroom 1, Bedroom 2 …)
     `mode: 'quantity'` → one card per room type with quantity + average area
     -------------------------------------------------------------------------- */
  const PROPERTY_TYPES = {
    home: {
      label: 'Home', glyph: '⌂', commercial: false, floors: true, hvac: false, nameLabel: null,
      groups: [{
        key: 'spaces', title: 'Spaces', mode: 'unit',
        options: ['Bedroom', 'Living Room', 'TV Lounge', 'Dining', 'Kitchen', 'Study', 'Office', 'Bathroom', 'Balcony', 'Gym', 'Other']
      }]
    },
    office: {
      label: 'Office', glyph: '▤', commercial: true, floors: true, hvac: true, nameLabel: 'Company name',
      groups: [{
        key: 'spaces', title: 'Spaces', mode: 'unit',
        options: ['Reception', 'CEO Office', 'Cabin', 'Meeting Room', 'Conference Room', 'Workspace', 'Pantry', 'Prayer Room', 'Washroom', 'Server Room', 'Other']
      }]
    },
    hotel: {
      label: 'Hotel', glyph: '✦', commercial: true, floors: true, hvac: true, nameLabel: 'Hotel name',
      groups: [
        {
          key: 'guest', title: 'Guest rooms', mode: 'quantity',
          options: ['Standard Room', 'Deluxe Room', 'Executive Suite', 'Presidential Suite', 'Other']
        },
        {
          key: 'public', title: 'Public areas', mode: 'unit',
          options: ['Lobby', 'Restaurant', 'Ballroom', 'Spa', 'Gym', 'Conference Hall', 'Reception', 'Corridor', 'Washroom', 'Other']
        }
      ]
    },
    restaurant: {
      label: 'Restaurant', glyph: '❋', commercial: true, floors: true, hvac: true, nameLabel: 'Restaurant name',
      groups: [{
        key: 'spaces', title: 'Spaces', mode: 'unit',
        options: ['Dining Hall', 'VIP', 'Kitchen', 'Coffee Counter', 'Bakery', 'Reception', 'Private Room', 'Washroom', 'Other']
      }]
    },
    spa: {
      label: 'Spa', glyph: '❀', commercial: false, floors: true, hvac: true, nameLabel: 'Spa name',
      groups: [{
        key: 'spaces', title: 'Spaces', mode: 'unit',
        options: ['Reception', 'Massage', 'Steam', 'Sauna', 'Hair Station', 'Facial', 'Treatment', 'Waiting', 'Washroom', 'Other']
      }]
    },
    gym: {
      label: 'Gym', glyph: '◆', commercial: false, floors: true, hvac: true, nameLabel: 'Gym name',
      groups: [{
        key: 'spaces', title: 'Spaces', mode: 'unit',
        options: ['Reception', 'Cardio', 'Weights', 'CrossFit', 'Studio', 'Yoga', 'Steam', 'Sauna', 'Changing', 'Washroom', 'Other']
      }]
    },
    retail: {
      label: 'Retail', glyph: '◈', commercial: false, floors: true, hvac: true, nameLabel: 'Store name',
      groups: [{
        key: 'spaces', title: 'Spaces', mode: 'unit',
        options: ['Entrance', 'Display', 'Sales Floor', 'Cash Counter', 'Trial Room', 'Stock', 'Office', 'Washroom', 'Other']
      }]
    },
    clinic: {
      label: 'Clinic', glyph: '✚', commercial: true, floors: true, hvac: true, nameLabel: 'Clinic name',
      groups: [{
        key: 'spaces', title: 'Spaces', mode: 'unit',
        options: ['Reception', 'Waiting', 'Consultation', 'Treatment', 'Laboratory', 'Pharmacy', 'Washroom', 'Other']
      }]
    }
  };

  const TOTAL_STEPS = 4;

  /* -------------------------------- helpers -------------------------------- */

  const readJSON = (root, selector, fallback) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      const parsed = JSON.parse(node.textContent);
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (err) {
      return fallback;
    }
  };

  const esc = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const toNumber = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };

  /**
   * The 9 moods from the "Smart Scent Recommendation" spec. Each mood's
   * keywords are matched (case-insensitive substring) against a scent's
   * title + notes to build recommendations — no fabricated products or
   * copy, only whatever is genuinely in the live Aroma 247 library.
   */
  const MOOD_LIBRARY = [
    { key: 'relax', label: 'Relax', emoji: '😌', tagline: 'Calm & peaceful', keywords: ['lavender', 'white tea', 'chamomile', 'soft wood', 'green tea', 'clean musk'] },
    { key: 'energize', label: 'Energize', emoji: '⚡', tagline: 'Fresh & lively', keywords: ['citrus', 'lemon', 'orange', 'bergamot', 'mint', 'fresh green', 'tropical'] },
    { key: 'focus', label: 'Focus', emoji: '🧠', tagline: 'Clear & focused', keywords: ['green tea', 'bergamot', 'citrus', 'rosemary', 'fresh wood', 'clean aromatic'] },
    { key: 'luxury', label: 'Luxury', emoji: '✨', tagline: 'Elegant & rich', keywords: ['oud', 'amber', 'leather', 'sandalwood', 'musk', 'rose', 'vanilla'] },
    { key: 'comfort', label: 'Comfort', emoji: '🏠', tagline: 'Warm & inviting', keywords: ['vanilla', 'cinnamon', 'soft musk', 'sandalwood', 'warm amber', 'bakery'] },
    { key: 'fresh', label: 'Fresh', emoji: '🌿', tagline: 'Clean & natural', keywords: ['citrus', 'green tea', 'lemongrass', 'ocean', 'eucalyptus', 'fresh linen'] },
    { key: 'romantic', label: 'Romantic', emoji: '💕', tagline: 'Intimate & warm', keywords: ['rose', 'vanilla', 'jasmine', 'musk', 'amber', 'sandalwood'] },
    { key: 'refresh', label: 'Refresh', emoji: '🌊', tagline: 'Cool & airy', keywords: ['ocean breeze', 'citrus', 'mint', 'green tea', 'rain', 'fresh linen'] },
    { key: 'traditional', label: 'Traditional', emoji: '🕌', tagline: 'Oud & oriental', keywords: ['oud', 'musk', 'rose', 'amber', 'sandalwood', 'bakhoor'] }
  ];

  const uid = () => 's' + Math.random().toString(36).slice(2, 9);

  /**
   * Given a required volume (ml) and a set of bottle sizes/prices, find the
   * combination that uses the FEWEST bottles while meeting or slightly
   * exceeding the requirement (never short). Ties on bottle count are
   * broken by the smallest surplus. Prices are never assumed — they come
   * straight from the settings the merchant configured.
   */
  function optimizeBottles(requiredMl, bottleSizes) {
    const sizes = (bottleSizes || [])
      .map((b) => ({ size: toNumber(b.size), price: toNumber(b.price) }))
      .filter((b) => b.size > 0)
      .sort((a, b) => a.size - b.size);

    if (!sizes.length || requiredMl <= 0) {
      return { lines: [], totalMl: 0, totalCost: 0 };
    }

    // Work in units of the smallest bottle size (all sizes here are multiples
    // of 100ml, so this keeps the DP table small regardless of order size).
    const unit = sizes.reduce((g, b) => gcd(g, b.size), sizes[0].size);
    const coins = sizes.map((b) => Math.round(b.size / unit));
    const targetUnits = Math.ceil(requiredMl / unit);
    const maxCoin = Math.max.apply(null, coins);
    const ceiling = targetUnits + maxCoin; // enough headroom to always find a match

    const INF = Infinity;
    const dp = new Array(ceiling + 1).fill(INF);
    const pick = new Array(ceiling + 1).fill(-1);
    dp[0] = 0;

    for (let v = 1; v <= ceiling; v++) {
      for (let i = 0; i < coins.length; i++) {
        const c = coins[i];
        if (c <= v && dp[v - c] + 1 < dp[v]) {
          dp[v] = dp[v - c] + 1;
          pick[v] = i;
        }
      }
    }

    let bestV = -1;
    for (let v = targetUnits; v <= ceiling; v++) {
      if (dp[v] < INF) { bestV = v; break; } // smallest reachable value >= target = smallest surplus
    }

    const counts = new Array(coins.length).fill(0);
    let v = bestV;
    while (v > 0 && pick[v] !== -1) {
      const i = pick[v];
      counts[i]++;
      v -= coins[i];
    }

    const lines = sizes
      .map((b, i) => ({ size: b.size, price: b.price, qty: counts[i] }))
      .filter((l) => l.qty > 0)
      .sort((a, b) => b.size - a.size);

    const totalMl = bestV * unit;
    const totalCost = lines.reduce((sum, l) => sum + (l.qty * l.price), 0);

    return { lines: lines, totalMl: totalMl, totalCost: totalCost };
  }

  function gcd(a, b) {
    a = Math.round(a); b = Math.round(b);
    while (b) { const t = b; b = a % b; a = t; }
    return a || 1;
  }

  /* ========================================================================== */

  class AromaConfigurator {
    constructor(root) {
      this.root = root;

      const catalogue = readJSON(root, '[data-aroma-diffusers]', []);
      this.diffusers = (Array.isArray(catalogue) && catalogue.length ? catalogue : FALLBACK_DIFFUSERS)
        .map((d) => ({
          id: d.id,
          title: d.title || d.productTitle || d.id,
          coverageMin: toNumber(d.coverageMin),
          coverageMax: toNumber(d.coverageMax) || toNumber(d.coverageMin),
          // Variant price (in cents) wins so the cart charges exactly what we show.
          price: d.variantPrice != null ? toNumber(d.variantPrice) / 100 : toNumber(d.price),
          metaPrice: toNumber(d.price),
          membershipPrice: toNumber(d.membershipPrice),
          installation: toNumber(d.installation),
          maintenance: toNumber(d.maintenance),
          image: d.image || '',
          sku: d.sku || '',
          fit: d.fit || 'std',
          // Oil consumption is a range for most real diffusers (e.g. 0.2–1.0
          // ml/hr). If the catalogue only supplies a single mlPerHour (no
          // min/max), treat that as both ends of the range so nothing here
          // has to guess or average.
          mlPerHourMin: toNumber(d.mlPerHourMin != null ? d.mlPerHourMin : d.mlPerHour) || 0,
          mlPerHourMax: toNumber(d.mlPerHourMax != null ? d.mlPerHourMax : d.mlPerHour) || 0,
          productHandle: d.productHandle || '',
          productUrl: d.productUrl || '',
          variantId: d.variantId || null,
          available: d.available !== false
        }))
        .sort((a, b) => a.coverageMax - b.coverageMax);

      const scentCatalogue = readJSON(root, '[data-aroma-scents]', []);
      this.scents = (Array.isArray(scentCatalogue) ? scentCatalogue : []).map((s) => ({
        id: s.id,
        title: s.title || s.id,
        categories: Array.isArray(s.categories) ? s.categories.filter(Boolean) : [],
        rating: toNumber(s.rating) || 5,
        notes: s.notes || '',
        price: toNumber(s.price),
        image: s.image || '',
        productHandle: s.productHandle || '',
        variantId: s.variantId || null,
        available: s.available !== false,
        // Real bottle sizes this specific fragrance is actually sold in
        // (parsed from its own Shopify variants) — used so the bottle
        // recommendation never suggests a size the product doesn't offer.
        sizes: (Array.isArray(s.sizes) ? s.sizes : [])
          .map((sz) => ({
            size: toNumber(sz.ml),
            price: toNumber(sz.price),
            variantId: sz.variantId || null,
            available: sz.available !== false
          }))
          .filter((sz) => sz.size > 0)
          .sort((a, b) => b.size - a.size)
      }));

      const settings = readJSON(root, '[data-aroma-settings]', {});
      this.settings = Object.assign({
        currency: 'Rs ',
        moneyFormat: '',
        shopCurrency: 'PKR',
        defaultHoursPerDay: 16,
        defaultDaysPerMonth: 26,
        installPerUnit: 2500,
        maintenancePerUnit: 4000,
        technicalVisitPerUnit: 1500,
        bottleSizes: [
          { size: 100, price: 1500 },
          { size: 300, price: 4000 },
          { size: 500, price: 6000 }
        ],
        membershipRate: 8,
        diffuserDiscountPercent: 14,
        scentDiscountPercent: 10,
        termMonths: 12,
        quoteText: 'Our scent consultants will prepare a custom quotation.',
        genericBottleName: 'Aroma Oil',
        cartUrl: '/cart',
        membership: { handle: PRODUCT_MAP.membership, title: 'Aroma 247 Membership', variantId: null, variantPrice: null }
      }, settings);

      // Membership monthly comes from the membership product when one is linked.
      const membershipVariantPrice = this.settings.membership && this.settings.membership.variantPrice;
      this.membershipUnitPrice = membershipVariantPrice != null ? toNumber(membershipVariantPrice) / 100 : null;

      this.state = {
        step: 1,
        propertyType: null,
        propertyName: '',
        floors: 1,
        hvac: null,
        spaces: [],
        overrides: {},          // spaceId -> diffuserId chosen manually
        customer: { name: '', phone: '', email: '', notes: '' },
        plan: 'once',
        editingSpaceId: null,
        usage: {
          hoursPerDay: toNumber(this.settings.defaultHoursPerDay) || 16,
          daysPerMonth: Math.max(22, Math.min(30, toNumber(this.settings.defaultDaysPerMonth) || 26))
        },
        usageTouched: false,    // becomes true once the customer actually adjusts hours/days — the auto fragrance estimate stays quiet until then
        scents: [],             // selected scent ids
        scentSize: {},          // scent id -> selected size in ml (defaults to largest in-stock size)
        scentMenuOpen: {},      // scent id -> whether its size-pick menu is currently open
        scentSearch: '',
        scentCategory: null,
        scentVisibleCount: 8,
        moodPrimary: null,      // global mood key
        moodSecondary: null,    // global secondary mood key
        roomMoods: {}           // space id -> mood key override for that room
      };

      this.cacheNodes();
      this.renderPropertyChoices();
      this.bindEvents();
      this.setupStickyVisibility();
      this.render();
    }

    /**
     * The mobile Continue bar is `position: fixed`, which means it stays
     * pinned to the viewport even once the whole configurator has scrolled
     * out of view — leaving it floating on top of the theme's page footer.
     * Watching the root section with an IntersectionObserver and hiding the
     * bar whenever no part of the section is on screen keeps it scoped to
     * the configurator, the way a sticky bar is expected to behave.
     */
    setupStickyVisibility() {
      if (!this.el.sticky || typeof IntersectionObserver !== 'function') return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.el.sticky.classList.toggle('is-scrolled-out', !entry.isIntersecting);
        });
      }, { threshold: 0 });
      observer.observe(this.root);
    }

    /* ------------------------------ node cache ------------------------------ */
    cacheNodes() {
      const q = (sel) => this.root.querySelector(sel);
      this.el = {
        steps: Array.from(this.root.querySelectorAll('[data-step]')),
        pills: Array.from(this.root.querySelectorAll('[data-step-pill]')),
        progress: q('[data-aroma-progress]'),
        progressTrack: q('[data-aroma-progress-track]'),
        propertyGrid: q('[data-aroma-property-grid]'),
        propertyDetails: q('[data-aroma-property-details]'),
        propertyFields: q('[data-aroma-property-fields]'),
        spacesCard: q('[data-aroma-spaces-card]'),
        spaceGroups: q('[data-aroma-space-groups]'),
        planBody: q('[data-aroma-plan-body]'),
        equipmentCard: q('[data-aroma-equipment-card]'),
        equipment: q('[data-aroma-equipment]'),
        quoteCard: q('[data-aroma-quote-card]'),
        propertySummary3: q('[data-aroma-property-summary]'),
        usageSliders: Array.from(this.root.querySelectorAll('[data-aroma-usage]')),
        usageSummary: q('[data-aroma-usage-summary]'),
        consumption: q('[data-aroma-consumption]'),
        scentCard: q('[data-aroma-scent-card]'),
        moodGrid: q('[data-aroma-mood-grid]'),
        roomMoods: q('[data-aroma-room-moods]'),
        moodProfile: q('[data-aroma-mood-profile]'),
        scentSearch: q('[data-aroma-scent-search]'),
        scentCats: q('[data-aroma-scent-cats]'),
        scentGrid: q('[data-aroma-scent-grid]'),
        scentShowMoreWrap: q('[data-aroma-scent-showmore]'),
        scentCollection: q('[data-aroma-scent-collection]'),
        scentMessage: q('[data-aroma-scent-message]'),
        finalSummary: q('[data-aroma-final-summary]'),
        compareBody: q('[data-aroma-compare-body]'),
        savings: q('[data-aroma-savings]'),
        onceHeadline: q('[data-aroma-once-headline]'),
        membershipHeadline: q('[data-aroma-membership-headline]'),
        plans: Array.from(this.root.querySelectorAll('[data-plan]')),
        prevBtn: q('[data-action="prev"]'),
        stepError: q('[data-aroma-step-error]'),
        cartMessage: q('[data-aroma-cart-message]'),
        checkoutHint: q('[data-aroma-checkout-hint]'),
        aside: this.root.querySelector('.aroma__aside'),
        summaryMobile: q('[data-summary-mobile="line"]'),
        summaryScents: q('[data-summary-scents]'),
        summaryScentsList: q('[data-summary-scents-list]'),
        stickyCta: this.root.querySelector('.aroma-sticky__cta'),
        sticky: this.root.querySelector('.aroma-sticky'),
        modal: q('[data-aroma-modal]'),
        modalBody: q('[data-aroma-modal-body]')
      };
    }

    /* -------------------------------- events -------------------------------- */
    bindEvents() {
      if (this.el.sticky) {
        this.root.addEventListener('focusin', (event) => {
          if (event.target.matches('input, textarea, select')) {
            this.el.sticky.classList.add('is-keyboard-open');
          }
        });
        this.root.addEventListener('focusout', (event) => {
          if (event.target.matches('input, textarea, select')) {
            this.el.sticky.classList.remove('is-keyboard-open');
          }
        });
      }

      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-action], [data-property], [data-add-space], [data-plan], [data-scent-cat]');

        // Close an open size menu on any click that isn't the menu itself
        // or the Select button that opened it.
        if (Object.keys(this.state.scentMenuOpen).some((k) => this.state.scentMenuOpen[k])) {
          const withinMenu = event.target.closest('.aroma-scent-size-menu');
          const isOpenTrigger = trigger && trigger.dataset.action === 'open-scent-size-menu';
          if (!withinMenu && !isOpenTrigger) {
            this.state.scentMenuOpen = {};
            this.renderScentLibrary();
          }
        }

        if (!trigger || !this.root.contains(trigger)) return;

        if (trigger.dataset.scentCat !== undefined) return this.selectScentCategory(trigger.dataset.scentCat);
        if (trigger.dataset.property) return this.selectProperty(trigger.dataset.property);
        if (trigger.dataset.addSpace) {
          return this.addSpace(trigger.dataset.group, trigger.dataset.addSpace);
        }
        if (trigger.dataset.plan) return this.selectPlan(trigger.dataset.plan);

        switch (trigger.dataset.action) {
          case 'next': return this.goNext();
          case 'prev': return this.goPrev();
          case 'remove-space': return this.removeSpace(trigger.dataset.id);
          case 'duplicate-space': return this.duplicateSpace(trigger.dataset.id);
          case 'change-diffuser': return this.openPicker(trigger.dataset.id);
          case 'pick-suggestion': return this.chooseForSpace(trigger.dataset.id, trigger.dataset.diffuser);
          case 'pick-diffuser': return this.pickDiffuser(trigger.dataset.diffuser);
          case 'close-modal': return this.closePicker();
          case 'toggle-summary': return this.toggleSummary(trigger);
          case 'add-once': return this.addToCart('once');
          case 'add-membership': return this.addToCart('membership');
          case 'toggle-scent': return this.toggleScent(trigger.dataset.scent);
          case 'pick-mood': return this.pickMood(trigger.dataset.mood);
          case 'pick-room-mood': return this.pickRoomMood(trigger.dataset.id, trigger.dataset.mood);
          case 'clear-room-mood': return this.clearRoomMood(trigger.dataset.id);
          case 'select-scent-size': return this.selectScentSize(trigger.dataset.scent, trigger.dataset.size);
          case 'open-scent-size-menu': return this.toggleScentSizeMenu(trigger.dataset.scent);
          case 'pick-scent-size': return this.pickScentSizeAndSelect(trigger.dataset.scent, trigger.dataset.size);
          case 'show-more-scents': return this.showMoreScents();
          default: return undefined;
        }
      });

      this.root.addEventListener('input', (event) => this.handleFieldChange(event));

      // Check contact fields as soon as the person leaves them.
      this.root.addEventListener('focusout', (event) => {
        const field = event.target;
        if (!field.dataset || !field.dataset.customer) return;
        const key = field.dataset.customer;
        if (!field.value.trim()) return;
        if (key === 'phone') {
          const result = this.checkPhone(field.value);
          if (!result.ok) this.flagField('phone', result.message);
        }
        if (key === 'email') {
          const result = this.checkEmail(field.value);
          if (!result.ok) this.flagField('email', result.message);
        }
      });
      this.root.addEventListener('change', (event) => this.handleFieldChange(event));

      // Plan cards are role="radio" elements, so Enter and Space must select them.
      this.root.addEventListener('keydown', (event) => {
        const plan = event.target.closest('[data-plan]');
        if (!plan) return;
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          this.selectPlan(plan.dataset.plan);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.el.modal.hidden) this.closePicker();
      });
    }

    handleFieldChange(event) {
      const field = event.target;

      if (field.dataset.aromaUsage) {
        const key = field.dataset.aromaUsage;
        const max = key === 'daysPerMonth' ? 30 : 24;
        const min = key === 'daysPerMonth' ? 22 : 0;
        this.state.usage[key] = Math.max(min, Math.min(max, toNumber(field.value)));
        this.state.usageTouched = true;
        this.renderUsageStep();
        this.renderConsumptionStep();
        this.renderFinalSummary();
        this.renderPricingStep();
        this.renderSummary();
        return;
      }

      if (field.dataset.aromaScentSearch !== undefined) {
        this.state.scentSearch = field.value;
        this.state.scentVisibleCount = 8;
        this.renderScentLibrary();
        return;
      }

      if (field.dataset.customer) {
        this.state.customer[field.dataset.customer] = field.value;
        field.classList.remove('is-invalid');
        const errorNode = this.root.querySelector('[data-error-for="' + field.dataset.customer + '"]');
        if (errorNode) errorNode.textContent = '';
        return;
      }

      if (field.dataset.propertyField) {
        const key = field.dataset.propertyField;
        if (key === 'floors') this.state.floors = Math.max(1, parseInt(field.value, 10) || 1);
        if (key === 'hvac') this.state.hvac = field.value;
        if (key === 'name') this.state.propertyName = field.value;
        this.renderSpaces();
        this.renderSummary();
        return;
      }

      if (field.dataset.spaceField) {
        const space = this.state.spaces.find((s) => s.id === field.dataset.id);
        if (!space) return;
        const key = field.dataset.spaceField;
        if (key === 'area') space.area = Math.max(0, toNumber(field.value));
        if (key === 'qty') space.qty = Math.max(1, parseInt(field.value, 10) || 1);
        if (key === 'floor') space.floor = field.value;
        if (key === 'name') space.name = field.value;
        if (key === 'area' || key === 'qty') delete this.state.overrides[space.id];
        this.updateRoomMatch(space);
        this.renderSummary();
      }
    }

    /* ------------------------------ step control ------------------------------ */
    goNext() {
      if (!this.validateStep(this.state.step)) return;
      if (this.state.step < TOTAL_STEPS) {
        this.state.step += 1;
        this.render();
        this.scrollToTop();
      }
    }

    goPrev() {
      if (this.state.step > 1) {
        this.state.step -= 1;
        this.setStepError('');
        this.render();
        this.scrollToTop();
      }
    }

    scrollToTop() {
      const top = this.root.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    validateStep(step) {
      this.setStepError('');

      if (step === 1) {
        if (!this.state.propertyType) {
          return this.setStepError('Choose a property type to continue.');
        }
        const config = PROPERTY_TYPES[this.state.propertyType];
        if (config.hvac && !this.state.hvac) {
          return this.setStepError('Let us know whether the property has HVAC.');
        }
        if (!this.state.spaces.length) {
          return this.setStepError('Add at least one space.');
        }
        const missingArea = this.state.spaces.find((s) => !s.area || s.area <= 0);
        if (missingArea) {
          return this.setStepError('Enter an area for every space.');
        }
        return true;
      }

      if (step === 2) {
        const { name, phone, email } = this.state.customer;
        let valid = true;

        if (name.trim().length < 2) {
          this.flagField('name', 'Enter your name.');
          valid = false;
        }

        const phoneCheck = this.checkPhone(phone);
        if (!phoneCheck.ok) {
          this.flagField('phone', phoneCheck.message);
          valid = false;
        } else {
          this.state.customer.phoneNormalized = phoneCheck.normalized;
        }

        const emailCheck = this.checkEmail(email);
        if (!emailCheck.ok) {
          this.flagField('email', emailCheck.message);
          valid = false;
        }

        if (!valid) this.setStepError('Complete the highlighted fields.');
        return valid;
      }

      return true;
    }

    /**
     * Reject numbers that are the right length but obviously invented:
     * all one digit, long runs, straight sequences, repeated blocks.
     */
    looksFake(digits) {
      if (/^(\d)\1+$/.test(digits)) return true;
      if (/(\d)\1{6,}/.test(digits)) return true;
      if (digits.length >= 7) {
        const ascending = '01234567890123456789';
        const descending = '98765432109876543210';
        if (ascending.indexOf(digits) !== -1 || descending.indexOf(digits) !== -1) return true;
      }
      if (/^(\d{2,3})\1{2,}$/.test(digits)) return true;
      return false;
    }

    /**
     * Validate a phone number, with proper rules for Pakistani numbers
     * and a sane fallback for international ones.
     * Returns { ok, message, normalized }.
     */
    checkPhone(raw) {
      const input = String(raw == null ? '' : raw).trim();

      if (!input) return { ok: false, message: 'Enter your phone number.' };
      if (/[A-Za-z]/.test(input)) return { ok: false, message: 'A phone number cannot contain letters.' };

      // Accept any punctuation the person likes: spaces, dashes, dots, brackets.
      let digits = input.replace(/\D/g, '');
      if (digits.length < 7) return { ok: false, message: 'That number is too short.' };
      if (digits.startsWith('00')) digits = digits.slice(2);

      // Only treat this as a Pakistani number when the input actually says so:
      // a +92 / 92 country code, a leading 0, or a bare 10-digit 3XXXXXXXXX.
      let national = null;
      let isPk = false;
      if (digits.startsWith('92') && digits.length > 10) {
        national = digits.slice(2);
        isPk = true;
      } else if (input.charAt(0) !== '+' && digits.startsWith('0')) {
        national = digits.slice(1);
        isPk = true;
      } else if (input.charAt(0) !== '+' && digits.length === 10 && digits.charAt(0) === '3') {
        national = digits;
        isPk = true;
      }

      // Someone may write the trunk 0 after the country code: +92 0307 …
      if (isPk && national && national.charAt(0) === '0') national = national.slice(1);

      // Pakistani mobile numbers: 3XXXXXXXXX after the leading 0 or +92.
      if (isPk && national && national.charAt(0) === '3') {
        if (national.length < 10) return { ok: false, message: 'That mobile number is too short. Example: 0307 3493905.' };
        if (national.length > 10) return { ok: false, message: 'That mobile number is too long. Example: 0307 3493905.' };
        // Live Pakistani mobile codes run 030x to 034x, plus 0355 for SCO.
        if (!/^3[0-5]/.test(national)) {
          return { ok: false, message: 'That is not a Pakistani mobile code. Numbers start 0300 to 0349.' };
        }
        if (this.looksFake(national)) return { ok: false, message: 'That number does not look real. Please check it.' };
        return { ok: true, message: '', normalized: '+92' + national };
      }

      // Pakistani landlines: area code plus 6 to 8 digits.
      if (isPk && national && national.length >= 9 && national.length <= 11) {
        if (this.looksFake(national)) return { ok: false, message: 'That number does not look real. Please check it.' };
        return { ok: true, message: '', normalized: '+92' + national };
      }

      // Anything else: treat as international.
      if (digits.length >= 8 && digits.length <= 15) {
        if (this.looksFake(digits)) return { ok: false, message: 'That number does not look real. Please check it.' };
        return { ok: true, message: '', normalized: '+' + digits };
      }

      return { ok: false, message: 'Enter a valid phone number, e.g. 0300 1234567.' };
    }

    checkEmail(raw) {
      const value = String(raw == null ? '' : raw).trim();
      if (!value) return { ok: false, message: 'Enter your email address.' };
      if (!/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(value)) {
        return { ok: false, message: 'Enter a valid email address, e.g. name@example.com.' };
      }
      if (/\.{2,}|@\./.test(value)) return { ok: false, message: 'Check the spelling of that address.' };
      return { ok: true, message: '' };
    }

    flagField(key, message) {
      const input = this.root.querySelector('[data-customer="' + key + '"]');
      const errorNode = this.root.querySelector('[data-error-for="' + key + '"]');
      if (input) input.classList.add('is-invalid');
      if (errorNode) errorNode.textContent = message;
      return true;
    }

    setStepError(message) {
      if (this.el.stepError) this.el.stepError.textContent = message;
      return false;
    }

    /* ---------------------------- property choice ---------------------------- */
    renderPropertyChoices() {
      this.el.propertyGrid.innerHTML = Object.keys(PROPERTY_TYPES).map((key) => {
        const config = PROPERTY_TYPES[key];
        return '<button type="button" class="aroma-choice" data-property="' + key + '">' +
          '<span class="aroma-choice__glyph" aria-hidden="true">' + config.glyph + '</span>' +
          esc(config.label) +
        '</button>';
      }).join('');
    }

    selectProperty(key) {
      if (this.state.propertyType === key) return;
      this.state.propertyType = key;
      this.state.spaces = [];
      this.state.overrides = {};
      this.state.hvac = null;
      this.state.propertyName = '';
      this.state.floors = 1;

      Array.from(this.el.propertyGrid.querySelectorAll('[data-property]')).forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.property === key);
      });

      this.setStepError('');
      this.renderPropertyFields();
      this.renderSpaces();
      this.renderSummary();
    }

    renderPropertyFields() {
      const config = PROPERTY_TYPES[this.state.propertyType];
      if (!config) {
        this.el.propertyDetails.hidden = true;
        return;
      }

      const parts = [];

      if (config.nameLabel) {
        parts.push(
          '<label class="aroma-field">' +
            '<span class="aroma-field__label">' + esc(config.nameLabel) + '</span>' +
            '<input type="text" class="aroma-input" data-property-field="name" value="' + esc(this.state.propertyName) + '" placeholder="' + esc(config.nameLabel) + '">' +
          '</label>'
        );
      }

      if (config.floors) {
        const options = [];
        for (let i = 1; i <= 30; i += 1) {
          options.push('<option value="' + i + '"' + (i === this.state.floors ? ' selected' : '') + '>' + i + (i === 1 ? ' floor' : ' floors') + '</option>');
        }
        parts.push(
          '<label class="aroma-field">' +
            '<span class="aroma-field__label">Number of floors</span>' +
            '<select class="aroma-select" data-property-field="floors">' + options.join('') + '</select>' +
          '</label>'
        );
      }

      if (config.hvac) {
        parts.push(
          '<label class="aroma-field">' +
            '<span class="aroma-field__label">HVAC system</span>' +
            '<select class="aroma-select" data-property-field="hvac">' +
              '<option value="">Select</option>' +
              '<option value="yes"' + (this.state.hvac === 'yes' ? ' selected' : '') + '>Yes, ducted HVAC</option>' +
              '<option value="no"' + (this.state.hvac === 'no' ? ' selected' : '') + '>No HVAC</option>' +
            '</select>' +
          '</label>'
        );
      }

      this.el.propertyFields.innerHTML = parts.join('');
      this.el.propertyDetails.hidden = parts.length === 0;
    }

    /* ------------------------------- spaces UI ------------------------------- */
    renderSpaces() {
      const config = PROPERTY_TYPES[this.state.propertyType];
      if (!config) {
        this.el.spacesCard.hidden = true;
        return;
      }
      this.el.spacesCard.hidden = false;

      this.el.spaceGroups.innerHTML = config.groups.map((group) => {
        const chips = group.options.map((option) => {
          const count = this.state.spaces.filter((s) => s.group === group.key && s.type === option).length;
          return '<button type="button" class="aroma-chip" data-add-space="' + esc(option) + '" data-group="' + esc(group.key) + '">' +
            esc(option) +
            (count ? '<span class="aroma-chip__count">' + count + '</span>' : '') +
          '</button>';
        }).join('');

        const rooms = this.state.spaces
          .filter((s) => s.group === group.key)
          .map((s) => this.roomMarkup(s, group))
          .join('');

        return '<div class="aroma-spacegroup">' +
          (config.groups.length > 1 ? '<h3 class="aroma-spacegroup__title">' + esc(group.title) + '</h3>' : '') +
          '<div class="aroma-chips">' + chips + '</div>' +
          '<div class="aroma-rooms">' + rooms + '</div>' +
        '</div>';
      }).join('');
    }

    roomMarkup(space, group) {
      const floorOptions = [];
      for (let i = 1; i <= this.state.floors; i += 1) {
        floorOptions.push('<option value="' + i + '"' + (String(space.floor) === String(i) ? ' selected' : '') + '>Floor ' + i + '</option>');
      }

      const isQuantity = group.mode === 'quantity';
      const match = this.matchDiffuser(space);

      const fields = [];

      if (isQuantity) {
        fields.push(this.fieldMarkup(space.id, 'qty', 'Quantity', 'number', space.qty, '1'));
        fields.push(this.fieldMarkup(space.id, 'area', 'Average area (sqft)', 'number', space.area || '', 'e.g. 320'));
      } else {
        fields.push(this.fieldMarkup(space.id, 'area', 'Area (sqft)', 'number', space.area || '', 'e.g. 420'));
        fields.push(
          '<label class="aroma-field">' +
            '<span class="aroma-field__label">Floor</span>' +
            '<select class="aroma-select" data-space-field="floor" data-id="' + space.id + '">' + floorOptions.join('') + '</select>' +
          '</label>'
        );
        fields.push(this.fieldMarkup(space.id, 'name', 'Name (optional)', 'text', space.name, 'e.g. Master bedroom'));
      }

      return '<div class="aroma-room" data-room="' + space.id + '">' +
        '<div class="aroma-room__head">' +
          '<h4 class="aroma-room__name">' + esc(this.spaceLabel(space)) + '</h4>' +
          '<div class="aroma-room__tools">' +
            '<button type="button" class="aroma-icon-btn" data-action="duplicate-space" data-id="' + space.id + '" title="Duplicate" aria-label="Duplicate ' + esc(this.spaceLabel(space)) + '">⧉</button>' +
            '<button type="button" class="aroma-icon-btn aroma-icon-btn--danger" data-action="remove-space" data-id="' + space.id + '" title="Remove" aria-label="Remove ' + esc(this.spaceLabel(space)) + '">×</button>' +
          '</div>' +
        '</div>' +
        '<div class="aroma-room__fields">' + fields.join('') + '</div>' +
        '<p class="aroma-room__match" data-match-for="' + space.id + '">' + this.matchText(space, match) + '</p>' +
      '</div>';
    }

    fieldMarkup(id, key, label, type, value, placeholder) {
      return '<label class="aroma-field">' +
        '<span class="aroma-field__label">' + esc(label) + '</span>' +
        '<input type="' + type + '"' + (type === 'number' ? ' min="' + (key === 'qty' ? 1 : 0) + '" step="1" inputmode="numeric"' : '') +
          ' class="aroma-input" data-space-field="' + key + '" data-id="' + id + '"' +
          ' value="' + esc(value) + '" placeholder="' + esc(placeholder) + '">' +
      '</label>';
    }

    matchText(space, diffuser) {
      if (!space.area) return 'Enter an area to see the matched diffuser.';
      if (!diffuser) return 'No diffuser covers this area yet — a consultant will advise.';
      return 'Matched to <strong>' + esc(diffuser.title) + '</strong> · covers up to ' + diffuser.coverageMax + ' sqft';
    }

    updateRoomMatch(space) {
      const node = this.root.querySelector('[data-match-for="' + space.id + '"]');
      if (node) node.innerHTML = this.matchText(space, this.matchDiffuser(space));

      const chipHost = this.el.spaceGroups;
      if (!chipHost) return;
      const heading = this.root.querySelector('[data-room="' + space.id + '"] .aroma-room__name');
      if (heading) heading.textContent = this.spaceLabel(space);
    }

    spaceLabel(space) {
      if (space.name && space.name.trim()) return space.name.trim();
      const siblings = this.state.spaces.filter((s) => s.group === space.group && s.type === space.type);
      if (siblings.length < 2) return space.type;
      return space.type + ' ' + (siblings.indexOf(space) + 1);
    }

    addSpace(groupKey, type) {
      const config = PROPERTY_TYPES[this.state.propertyType];
      const group = config.groups.find((g) => g.key === groupKey);
      if (!group) return;

      this.state.spaces.push({
        id: uid(),
        group: groupKey,
        type: type,
        name: '',
        area: '',
        floor: 1,
        qty: group.mode === 'quantity' ? 1 : 1
      });

      this.setStepError('');
      this.renderSpaces();
      this.renderSummary();
    }

    removeSpace(id) {
      this.state.spaces = this.state.spaces.filter((s) => s.id !== id);
      delete this.state.overrides[id];
      delete this.state.roomMoods[id];
      this.renderSpaces();
      this.renderSummary();
    }

    duplicateSpace(id) {
      const index = this.state.spaces.findIndex((s) => s.id === id);
      if (index === -1) return;
      const copy = Object.assign({}, this.state.spaces[index], { id: uid(), name: '' });
      this.state.spaces.splice(index + 1, 0, copy);
      this.renderSpaces();
      this.renderSummary();
    }

    /* --------------------------- recommendation engine ---------------------------
       Rule: pick the smallest unit whose coverage reaches the room's area.
       If the room is larger than every unit, take the largest unit and fit
       as many as the room needs. HVAC units are only offered when the
       property has ducted HVAC, and only HVAC units are offered then for
       rooms big enough to justify one.
       ---------------------------------------------------------------------- */

    /** Units eligible for this property's HVAC answer — in-stock only. */
    eligibleDiffusers() {
      const hasHvac = this.state.hvac === 'yes';
      const inStock = this.diffusers.filter((d) => d.available);
      const pool = inStock.filter((d) => (hasHvac ? true : d.fit !== 'hvac'));
      return pool.length ? pool : inStock;
    }

    /**
     * Builds the one synthetic "combo" pick: exactly 1× Scent Splash +
     * 1× Sensei together, standing in as a single diffuser-shaped object
     * (title/coverageMax/price/mlPerHour) so the rest of the engine
     * (plan rows, totals, oil-consumption maths) can treat it exactly
     * like any other diffuser without special-casing every call site.
     * Only ever used for these two specific products.
     */
    comboDiffuserFor(splash, sensei) {
      return {
        id: 'combo-splash-sensei',
        title: splash.title + ' + ' + sensei.title,
        coverageMax: splash.coverageMax + sensei.coverageMax,
        price: toNumber(splash.price) + toNumber(sensei.price),
        mlPerHourMin: toNumber(splash.mlPerHourMin) + toNumber(sensei.mlPerHourMin),
        mlPerHourMax: toNumber(splash.mlPerHourMax) + toNumber(sensei.mlPerHourMax),
        available: true,
        isCombo: true,
        parts: [
          { diffuser: splash, units: 1 },
          { diffuser: sensei, units: 1 }
        ]
      };
    }

    /**
     * The engine's own pick for a room, ignoring any manual choice.
     *
     * SPECIAL CASE — Sensei (plug-in) and Scent Splash only: whenever a
     * room is bigger than one Scent Splash on its own (>750 sqft) but no
     * more than 1,000 sqft — i.e. exactly the gap the 1,250 sqft unit
     * being out of stock currently leaves — recommend 1× Scent Splash +
     * 1× Sensei together (750 + 250 = 1,000 sqft covered) instead of
     * jumping straight to the next size class up. Everywhere else keeps
     * the exact "smallest single unit that covers the room" behaviour it
     * always had.
     */
    recommendedFor(space) {
      const area = toNumber(space.area);
      if (!area) return null;
      const pool = this.eligibleDiffusers();
      if (!pool.length) return null;

      if (area > 750 && area <= 1000) {
        const combo = this.comboFromPool();
        if (combo) return combo;
      }

      return pool.find((d) => area <= d.coverageMax) || pool[pool.length - 1];
    }

    matchDiffuser(space) {
      const area = toNumber(space.area);
      if (!area) return null;

      const override = this.state.overrides[space.id];
      if (override) {
        if (override === 'combo-splash-sensei') {
          const combo = this.comboFromPool();
          if (combo) return combo;
        } else {
          const chosen = this.diffusers.find((d) => d.id === override && d.available);
          if (chosen) return chosen;
        }
      }

      return this.recommendedFor(space);
    }

    /** The Scent Splash + Sensei combo, built from whatever is currently in stock — or null if either is unavailable. */
    comboFromPool() {
      const pool = this.eligibleDiffusers();
      const splash = pool.find((d) => d.productHandle === 'scent-splash-scent-diffuser-machine-1');
      const sensei = pool.find((d) => d.productHandle === 'sensei-plug-in-room-diffuser');
      return (splash && sensei) ? this.comboDiffuserFor(splash, sensei) : null;
    }

    /**
     * Up to four ranked options for a room: the best fit, one cheaper unit
     * below it (which may need doubling up), and the next sizes up.
     * Returned smallest to largest.
     *
     * SPECIAL CASE — Sensei (plug-in) and Scent Splash only: for a
     * 750–1,000 sqft room these two are forced into the list together
     * (multiplied up as needed) even if the generic window above
     * wouldn't have reached both, since a multi-unit combination of
     * either is a genuinely cheaper alternative to jumping straight to
     * the next size class. The combined "1× Splash + 1× Sensei" pick
     * (comboFromPool) is included here too, so it shows up as its own
     * selectable option in this table — not just as the default match
     * text elsewhere — and can carry the "Best fit" flag. No other
     * product's suggestion set is widened — everything else keeps the
     * exact behaviour it had before.
     */
    suggestionsFor(space, limit) {
      const max = limit || 4;
      const area = toNumber(space.area);
      const pool = this.eligibleDiffusers();
      if (!pool.length || !area) return [];

      const bestIndex = pool.findIndex((d) => area <= d.coverageMax);
      const picks = [];

      if (bestIndex === -1) {
        // Bigger than anything we sell: offer the largest few, multiplied.
        return pool.slice(Math.max(0, pool.length - max));
      }

      // One step below, so there is a lower-cost option on the table.
      if (bestIndex > 0) picks.push(pool[bestIndex - 1]);
      picks.push(pool[bestIndex]);

      let next = bestIndex + 1;
      while (picks.length < max && next < pool.length) {
        picks.push(pool[next]);
        next += 1;
      }

      // Still short (best fit is near the top): pad downwards.
      let back = bestIndex - 2;
      while (picks.length < max && back >= 0) {
        picks.unshift(pool[back]);
        back -= 1;
      }

      const finalPicks = picks.slice(0, max);

      if (area >= 750 && area <= 1000) {
        ['sensei-plug-in-room-diffuser', 'scent-splash-scent-diffuser-machine-1'].forEach((handle) => {
          const match = pool.find((d) => d.productHandle === handle);
          if (match && finalPicks.indexOf(match) === -1) finalPicks.push(match);
        });
        if (area > 750) {
          const combo = this.comboFromPool();
          if (combo) finalPicks.push(combo);
        }
        finalPicks.sort((a, b) => a.coverageMax - b.coverageMax);
      }

      return finalPicks;
    }

    /** How many of this unit a single room of this size needs. */
    unitsForRoom(area, diffuser) {
      if (!diffuser || !diffuser.coverageMax || !area) return 1;
      return Math.max(1, Math.ceil(area / diffuser.coverageMax));
    }

    buildPlan() {
      return this.state.spaces.map((space) => {
        const diffuser = this.matchDiffuser(space);
        const rooms = Math.max(1, parseInt(space.qty, 10) || 1);
        const area = toNumber(space.area);
        const perRoom = this.unitsForRoom(area, diffuser);
        const units = perRoom * rooms;
        const price = diffuser ? diffuser.price : 0;

        return {
          space: space,
          label: this.spaceLabel(space),
          area: area,
          rooms: rooms,
          perRoom: perRoom,
          units: units,
          totalArea: area * rooms,
          diffuser: diffuser,
          subtotal: price * units,
          coverage: diffuser ? diffuser.coverageMax * units : 0,
          isOverridden: Boolean(this.state.overrides[space.id])
        };
      });
    }

    totals() {
      const plan = this.buildPlan();
      const months = this.settings.termMonths;

      let units = 0;
      let area = 0;
      let coverage = 0;
      let equipment = 0;
      let membershipMonthly = 0;

      /* ---------------- usage schedule -> runtime ---------------- */
      const hoursPerDay = toNumber(this.state.usage.hoursPerDay);
      const daysPerMonth = toNumber(this.state.usage.daysPerMonth);
      const monthlyRuntimeHours = hoursPerDay * daysPerMonth;
      const annualRuntimeHours = monthlyRuntimeHours * 12;

      /* ---------------- per-diffuser oil consumption ---------------- */
      // Each diffuser type has its own ml/hr burn rate — usually published
      // as a range (e.g. 0.2–1.0 ml/hr), not a single number — so this
      // tracks the low and high ends separately per row and sums them.
      // Nothing here is averaged: totalYearlyOilMlMin uses every row's own
      // mlPerHourMin, totalYearlyOilMlMax uses every row's own mlPerHourMax.
      // totalYearlyOilMl (the max) is what downstream fragrance/bottle
      // planning uses, so recommended bottle quantities always cover the
      // diffuser's highest published burn rate rather than running short.
      let totalMonthlyOilMl = 0;
      let totalMonthlyOilMlMin = 0;
      let totalYearlyOilMl = 0;
      let totalYearlyOilMlMin = 0;

      plan.forEach((row) => {
        area += row.totalArea;
        if (!row.diffuser) return;
        units += row.units;
        coverage += row.coverage;
        equipment += row.subtotal;

        const mlPerHourMin = toNumber(row.diffuser.mlPerHourMin);
        const mlPerHourMax = toNumber(row.diffuser.mlPerHourMax);
        const monthlyMlMinForRow = mlPerHourMin * monthlyRuntimeHours * row.units;
        const monthlyMlMaxForRow = mlPerHourMax * monthlyRuntimeHours * row.units;
        totalMonthlyOilMlMin += monthlyMlMinForRow;
        totalMonthlyOilMl += monthlyMlMaxForRow;
        totalYearlyOilMlMin += monthlyMlMinForRow * 12;
        totalYearlyOilMl += monthlyMlMaxForRow * 12;
      });

      const hasConsumptionRange = Math.round(totalYearlyOilMlMin) !== Math.round(totalYearlyOilMl);

      // Membership monthly: the linked product's price per diffuser if one
      // exists, otherwise a percentage of the equipment value. This is the
      // real recurring subscription price (separate from the first-year
      // comparison below, which uses member pricing on diffusers + oil).
      if (this.membershipUnitPrice != null && units) {
        membershipMonthly = this.membershipUnitPrice * units;
      } else {
        membershipMonthly = equipment * (toNumber(this.settings.membershipRate) / 100);
      }

      /* ---------------- bottle optimisation engine ---------------- */
      // Each selected fragrance is costed using ONLY the bottle sizes that
      // fragrance is actually sold in (its own Shopify variants). The
      // required yearly volume is split evenly across whichever scents are
      // selected. If nothing is selected yet, fall back to the theme's
      // generic size table so the card still shows a rough estimate.
      const globalBottleSizes = Array.isArray(this.settings.bottleSizes) ? this.settings.bottleSizes : [];
      const selectedScents = this.scents.filter((s) => this.state.scents.indexOf(s.id) !== -1);

      let fragranceBreakdown = [];
      let fragranceCost = 0;
      let combinedBottleLines = [];

      if (selectedScents.length) {
        const shareMl = totalYearlyOilMl / selectedScents.length;
        fragranceBreakdown = selectedScents.map((s) => {
          // Only ever cost this fragrance using sizes it is actually sold
          // in AND currently has in stock. If Shopify hasn't given us real
          // variant sizes for it, or none of its sizes are in stock, skip
          // the bottle estimate rather than guessing at a size (e.g. a
          // 1000ml tier) that this specific product may not offer or that
          // is currently out of stock.
          const availableSizes = s.sizes.filter((sz) => sz.available);
          const plan = availableSizes.length ? optimizeBottles(shareMl, availableSizes) : { lines: [], totalMl: 0, totalCost: 0 };
          // unitPrice drives the real totals/checkout math, and must
          // always match the bottle(s) actually worked out for this
          // fragrance's share of the yearly volume — not a fixed
          // "largest size" reference price. Otherwise the cost never
          // moves when the required ml (and so the bottle size/qty)
          // changes, and the cart (which adds exactly these bottles)
          // would no longer match the total shown here.
          //
          // referencePrice is separate and purely informational: the
          // product's own single listed price exactly as Shopify shows
          // it (its default/first-available variant) — used only where
          // we want to show "here's roughly what this oil costs" next
          // to its name, not the full annual bottle-plan total.
          return { scent: s, requiredMl: shareMl, bottlePlan: plan, unitPrice: plan.totalCost, referencePrice: toNumber(s.price) };
        });

        // The fragrance cost is the sum of what each selected fragrance's
        // own bottle plan actually costs (its real bottle sizes × Shopify
        // variant prices for the ml this plan actually needs).
        fragranceCost = fragranceBreakdown.reduce((sum, f) => sum + toNumber(f.unitPrice), 0);

        // Merge every scent's bottle lines into one combined list (by size)
        // purely for the simple "Bottle recommendation" summary readout.
        const merged = {};
        fragranceBreakdown.forEach((f) => {
          f.bottlePlan.lines.forEach((l) => {
            if (!merged[l.size]) merged[l.size] = { size: l.size, price: l.price, qty: 0 };
            merged[l.size].qty += l.qty;
          });
        });
        combinedBottleLines = Object.keys(merged).map((k) => merged[k]).sort((a, b) => b.size - a.size);
      } else {
        // No fragrance has been manually selected yet, so there is nothing
        // real to cost. Showing a generic estimate here (built from the
        // theme's fallback bottle sizes) produced a fragrance figure that
        // had nothing to do with what the customer actually chose, which
        // then bled into the equipment total, the summary sidebar and the
        // Buy once / Membership comparison. Only selected fragrances are
        // ever costed.
        fragranceCost = 0;
        combinedBottleLines = [];
      }

      const bottlePlan = { lines: combinedBottleLines, totalCost: fragranceCost };

      /* ---------------- fragrance estimate (manual selection only) ----------------
         Per the current design, oils are never auto-selected. The
         "Number of fragrance bottles" / "Estimated fragrance cost" figures
         only ever reflect the fragrance(s) the customer has actually
         picked in the signature scent library (fragranceBreakdown above).
         Until at least one scent is selected, there is nothing real to
         show here yet. */
      const fragranceEstimateParts = fragranceBreakdown.reduce((acc, f) => acc.concat(f.bottlePlan.lines.map((l) => ({
        label: f.scent.title + ' ' + l.size + 'ML' + (l.qty > 1 ? ' ×' + l.qty : ''),
        qty: l.qty
      }))), []);
      const fragranceEstimateQty = fragranceEstimateParts.reduce((sum, p) => sum + p.qty, 0);
      const fragranceEstimateLabel = fragranceEstimateParts.length
        ? fragranceEstimateParts.map((p) => p.label).join(', ') + ' (' + fragranceEstimateQty + ' bottle' + (fragranceEstimateQty === 1 ? '' : 's') + ')'
        : '—';
      const fragranceEstimate = {
        label: fragranceEstimateLabel,
        totalCost: fragranceCost,
        qty: fragranceEstimateQty
      };

      // Rough monthly bottle count, purely for the "estimated monthly
      // consumption" readout — the yearly bottlePlan above is what's
      // actually costed and purchased.
      const sizePool = selectedScents.length
        ? selectedScents.reduce((acc, s) => acc.concat(s.sizes), [])
        : globalBottleSizes;
      const smallestBottle = sizePool.length ? Math.min.apply(null, sizePool.map((b) => b.size)) : 0;
      const monthlyBottles = smallestBottle > 0 ? Math.ceil(totalMonthlyOilMl / smallestBottle) : 0;
      const annualBottles = combinedBottleLines.reduce((sum, l) => sum + l.qty, 0);

      let recommendedMembership = 'Signature Membership';
      if (annualBottles > 24) recommendedMembership = 'Elite Membership';
      else if (annualBottles > 12) recommendedMembership = 'Premium Membership';

      /* ---------------- member pricing on diffusers & fragrance ---------------- */
      const diffuserDiscountPercent = toNumber(this.settings.diffuserDiscountPercent);
      const scentDiscountPercent = toNumber(this.settings.scentDiscountPercent);
      const equipmentMemberPrice = equipment * (1 - diffuserDiscountPercent / 100);
      const fragranceMemberCost = fragranceCost * (1 - scentDiscountPercent / 100);

      /* ---------------- first-year totals ---------------- */
      // Buy once: equipment + fragrance only. Installation, maintenance and
      // technical visits are handled separately and are not part of this
      // comparison. Membership: diffusers and fragrance at member pricing.
      const onceTotal = equipment + fragranceCost;
      const membershipTotal = equipmentMemberPrice + fragranceMemberCost;
      const membershipTerm = membershipTotal; // kept for backward compatibility with older UI bindings

      /* ---------------- grand total (reflects the selected plan) ---------------- */
      // Buy once keeps showing the full equipment + fragrance figure exactly
      // as before. Membership now shows the same discounted pricing used in
      // the Buy once vs Membership comparison above (equipmentMemberPrice /
      // scentDiscountPercent), so the sidebar total always matches whichever
      // plan card is currently selected instead of always showing the Buy
      // once figure.
      const fragranceEstimateMemberCost = fragranceEstimate.totalCost * (1 - scentDiscountPercent / 100);
      const equipmentForPlan = this.state.plan === 'membership' ? equipmentMemberPrice : equipment;
      const fragranceEstimateForPlan = this.state.plan === 'membership' ? fragranceEstimateMemberCost : fragranceEstimate.totalCost;
      const grandTotal = equipmentForPlan + fragranceEstimateForPlan;

      return {
        plan: plan,
        months: months,
        units: units,
        area: area,
        coverage: coverage,
        equipment: equipment,
        hoursPerDay: hoursPerDay,
        daysPerMonth: daysPerMonth,
        monthlyRuntimeHours: monthlyRuntimeHours,
        annualRuntimeHours: annualRuntimeHours,
        totalMonthlyOilMl: totalMonthlyOilMl,
        totalMonthlyOilMlMin: totalMonthlyOilMlMin,
        totalYearlyOilMl: totalYearlyOilMl,
        totalYearlyOilMlMin: totalYearlyOilMlMin,
        hasConsumptionRange: hasConsumptionRange,
        bottlePlan: bottlePlan,
        monthlyBottles: monthlyBottles,
        annualBottles: annualBottles,
        recommendedMembership: recommendedMembership,
        fragranceCost: fragranceCost,
        onceTotal: onceTotal,
        membershipMonthly: membershipMonthly,
        membershipTotal: membershipTotal,
        membershipTerm: membershipTerm,
        diffuserDiscountPercent: diffuserDiscountPercent,
        scentDiscountPercent: scentDiscountPercent,
        equipmentMemberPrice: equipmentMemberPrice,
        equipmentMemberSavings: equipment - equipmentMemberPrice,
        fragranceMemberCost: fragranceMemberCost,
        fragranceCostSavings: fragranceCost - fragranceMemberCost,
        selectedScents: selectedScents,
        fragranceBreakdown: fragranceBreakdown,
        fragranceEstimate: fragranceEstimate,
        equipmentForPlan: equipmentForPlan,
        fragranceEstimateForPlan: fragranceEstimateForPlan,
        grandTotal: grandTotal,
        savings: onceTotal - membershipTotal
      };
    }

    /**
     * Format a figure using the shop currency symbol.
     * Whole units only — no decimals, which suits PKR pricing.
     */
    money(value) {
      const rounded = Math.round(toNumber(value));
      const symbol = this.settings.currency || 'Rs ';
      const needsSpace = /[A-Za-z]$/.test(symbol.trim()) && !/\s$/.test(symbol);
      return symbol + (needsSpace ? ' ' : '') + rounded.toLocaleString('en-US');
    }

    /* --------------------------------- render --------------------------------- */
    render() {
      this.el.steps.forEach((node) => {
        node.classList.toggle('is-active', Number(node.dataset.step) === this.state.step);
      });

      this.el.pills.forEach((pill) => {
        const index = Number(pill.dataset.stepPill);
        pill.classList.toggle('is-active', index === this.state.step);
        pill.classList.toggle('is-done', index < this.state.step);
      });

      const pct = (this.state.step / TOTAL_STEPS) * 100;
      this.el.progress.style.width = pct + '%';
      this.el.progressTrack.setAttribute('aria-valuenow', String(this.state.step));

      this.el.prevBtn.hidden = this.state.step === 1;

      const isLast = this.state.step === TOTAL_STEPS;
      const nextButtons = Array.from(this.root.querySelectorAll('[data-action="next"]'));
      nextButtons.forEach((btn) => {
        btn.hidden = isLast;
        btn.textContent = this.state.step === 3 ? 'See pricing' : 'Continue';
      });

      if (this.state.step === 3) {
        this.renderUsageStep();
        this.renderPlanStep();
        this.renderMoodStep();
        this.renderScentLibrary();
        this.renderConsumptionStep();
        this.renderFinalSummary();
      }
      if (this.state.step === 4) this.renderPricingStep();

      this.renderSummary();
    }

    renderPlanStep() {
      const totals = this.totals();
      const commercial = this.isCommercial();
      const config = PROPERTY_TYPES[this.state.propertyType];

      if (this.el.propertySummary3) {
        const setP = (key, value) => {
          const node = this.el.propertySummary3.querySelector('[data-summary3="' + key + '"]');
          if (node) node.textContent = value;
        };
        setP('property', config ? (this.state.propertyName || config.label) : '—');
        setP('spaces', String(this.state.spaces.reduce((sum, s) => sum + (parseInt(s.qty, 10) || 1), 0)));
        setP('area', totals.area.toLocaleString('en-US') + ' sqft');
      }

      this.el.planBody.innerHTML = totals.plan.length
        ? totals.plan.map((row) => {
            const d = row.diffuser;
            const areaText = row.rooms > 1
              ? row.rooms + ' × ' + row.area.toLocaleString('en-US') + ' sqft'
              : (row.area ? row.area.toLocaleString('en-US') + ' sqft' : '—');

            const picks = this.suggestionsFor(row.space);
            const recommended = picks.length ? this.recommendedFor(row.space) : null;

            // Cheapest fully-covering option among those shown, so a
            // multi-unit combo that beats the coverage-based "Best fit"
            // on price is flagged too — recommendations shouldn't be
            // coverage-only, price has to factor in.
            let bestValueId = null;
            let bestValuePrice = Infinity;
            picks.forEach((opt) => {
              const totalUnits = this.unitsForRoom(row.area, opt) * row.rooms;
              const totalPrice = toNumber(opt.price) * totalUnits;
              if (opt.available && totalPrice > 0 && totalPrice < bestValuePrice) {
                bestValuePrice = totalPrice;
                bestValueId = opt.id;
              }
            });

            const picksHtml = picks.length
              ? '<div class="aroma-picks" role="radiogroup" aria-label="Diffuser options for ' + esc(row.label) + '">' +
                  picks.map((opt) => {
                    const perRoom = this.unitsForRoom(row.area, opt);
                    const totalUnits = perRoom * row.rooms;
                    const isOn = d && d.id === opt.id;
                    const isRec = recommended && recommended.id === opt.id;
                    const isBestValue = bestValueId === opt.id && !isRec;
                    return '<button type="button" class="aroma-pick' + (isOn ? ' is-selected' : '') + '"' +
                      ' role="radio" aria-checked="' + (isOn ? 'true' : 'false') + '"' +
                      ' data-action="pick-suggestion" data-id="' + row.space.id + '" data-diffuser="' + esc(opt.id) + '">' +
                      '<span class="aroma-pick__dot" aria-hidden="true"></span>' +
                      '<span class="aroma-pick__body">' +
                        '<span class="aroma-pick__name">' + esc(opt.title) +
                          (isRec ? '<span class="aroma-pick__flag">Best fit</span>' : '') +
                          (isBestValue ? '<span class="aroma-pick__flag">Best value</span>' : '') +
                        '</span>' +
                        '<span class="aroma-pick__meta">Up to ' + opt.coverageMax.toLocaleString('en-US') + ' sqft' +
                          (totalUnits > 1 ? ' · ' + totalUnits + ' units needed' : '') +
                          (!opt.available ? ' · out of stock' : '') +
                        '</span>' +
                      '</span>' +
                      '<span class="aroma-pick__price">' + this.money(opt.price * totalUnits) + '</span>' +
                    '</button>';
                  }).join('') +
                '</div>'
              : '<em>Enter an area to see options</em>';

            return '<tr>' +
              '<td class="aroma-table__space" data-label="Space">' + esc(row.label) +
                '<span class="aroma-table__sub">' + esc(row.space.type) + (row.space.floor && row.space.group !== 'guest' ? ' · Floor ' + esc(row.space.floor) : '') + '</span>' +
              '</td>' +
              '<td class="aroma-table__area" data-label="Area">' + areaText + '</td>' +
              '<td class="aroma-table__options" data-label="Suggested diffusers">' + picksHtml +
                (d && !d.variantId ? '<span class="aroma-table__sub aroma-table__warn">Not linked to a product</span>' : '') +
              '</td>' +
              '<td class="aroma-table__num" data-label="Units">' + (d ? row.units : '—') + '</td>' +
              '<td class="aroma-table__num" data-label="Subtotal">' + (d ? this.money(row.subtotal) : '—') + '</td>' +
              '<td class="aroma-table__num aroma-table__act">' +
                '<button type="button" class="aroma-more-btn" data-action="change-diffuser" data-id="' + row.space.id + '" title="View all diffusers" aria-label="View all diffusers for ' + esc(row.label) + '">' +
                  'More Options' +
                '</button>' +
              '</td>' +
            '</tr>';
          }).join('') +
          '<tr class="aroma-table__total">' +
            '<td colspan="3">Total</td>' +
            '<td class="aroma-table__num" data-label="Total units">' + totals.units + '</td>' +
            '<td class="aroma-table__num" data-label="Total">' + this.money(totals.equipment) + '</td>' +
            '<td></td>' +
          '</tr>'
        : '<tr><td colspan="6" class="aroma-empty">No spaces added yet. Go back to step 1 to add them.</td></tr>';

      // Commercial projects get a quotation note instead of the equipment table.
      this.el.equipmentCard.hidden = commercial;
      this.el.quoteCard.hidden = !commercial;

      if (!commercial) {
        const grouped = {};
        totals.plan.forEach((row) => {
          if (!row.diffuser || !row.units) return;
          // Show a combo row (Scent Splash + Sensei) as its two real
          // products, each with its own image/price — not one fused line.
          const parts = row.diffuser.isCombo ? row.diffuser.parts : [{ diffuser: row.diffuser, units: 1 }];
          parts.forEach((part) => {
            if (!part.diffuser) return;
            const key = part.diffuser.id;
            if (!grouped[key]) grouped[key] = { diffuser: part.diffuser, qty: 0 };
            grouped[key].qty += part.units * row.units;
          });
        });

        const units = Object.keys(grouped).map((key) => grouped[key]);
        this.el.equipment.innerHTML = units.length
          ? units.map((unit) =>
              '<div class="aroma-unit">' +
                (unit.diffuser.image
                  ? '<img class="aroma-unit__img" src="' + esc(unit.diffuser.image) + '" alt="' + esc(unit.diffuser.title) + '" loading="lazy" onerror="this.style.display=\'none\'">'
                  : '<span class="aroma-unit__qty">' + unit.qty + '×</span>') +
                '<div>' +
                  '<div class="aroma-unit__name">' + (unit.diffuser.image ? unit.qty + ' × ' : '') + esc(unit.diffuser.title) + '</div>' +
                  '<div class="aroma-unit__meta">' + this.money(unit.diffuser.price * unit.qty) + (unit.diffuser.sku ? ' · ' + esc(unit.diffuser.sku) : '') + '</div>' +
                '</div>' +
              '</div>'
            ).join('')
          : '<p class="aroma-empty">Add areas to your spaces to see recommended equipment.</p>';
      }
    }

    /* ------------------------------ 3.2 usage ------------------------------ */
    renderUsageStep() {
      const t = this.totals();

      this.el.usageSliders.forEach((slider) => {
        const key = slider.dataset.aromaUsage;
        const value = this.state.usage[key];
        if (String(slider.value) !== String(value)) slider.value = value;
        const readout = this.root.querySelector('[data-aroma-usage-readout="' + key + '"]');
        if (readout) {
          readout.textContent = key === 'hoursPerDay'
            ? value + ' hours'
            : value + ' days';
        }
      });

      if (this.el.usageSummary) {
        this.el.usageSummary.innerHTML =
          '<div class="aroma-usage-summary__row"><span>Operating schedule</span><strong>' + t.hoursPerDay + ' Hours · ' + t.daysPerMonth + ' Days</strong></div>' +
          '<div class="aroma-usage-summary__row"><span>Estimated monthly runtime</span><strong>' + t.monthlyRuntimeHours.toLocaleString('en-US') + ' hours</strong></div>' +
          '<div class="aroma-usage-summary__row"><span>Estimated annual runtime</span><strong>' + t.annualRuntimeHours.toLocaleString('en-US') + ' hours</strong></div>';
      }
    }

    /* --------------------------- 3.3 consumption --------------------------- */
    renderConsumptionStep() {
      if (!this.el.consumption) return;
      const t = this.totals();

      // Number of diffusers is a direct total. The fragrance bottle count
      // and cost come from t.fragranceEstimate, which only reflects the
      // signature scent(s) the customer has manually picked — until at
      // least one scent is selected this reads as "—".
      this.el.consumption.innerHTML =
        '<div class="aroma-consumption__row"><span>Number of diffusers</span><strong>' + t.units + '</strong></div>' +
        '<div class="aroma-consumption__row"><span>Number of fragrance bottles</span><strong>' + esc(t.fragranceEstimate.label) + '</strong></div>' +
        '<div class="aroma-consumption__row"><span>Estimated annual fragrance cost</span><strong>' + (t.fragranceEstimate.totalCost > 0 ? this.money(t.fragranceEstimate.totalCost) : '—') + '</strong></div>' +
        '<div class="aroma-consumption__row aroma-consumption__row--flag"><span>Recommended membership</span><strong>' + esc(t.recommendedMembership) + '</strong></div>';
    }

    /* ---------------------------- 3.4 scent library ---------------------------- */
    selectScentCategory(cat) {
      this.state.scentCategory = this.state.scentCategory === cat ? null : cat;
      this.state.scentVisibleCount = 8;
      this.renderScentLibrary();
    }

    showMoreScents() {
      this.state.scentVisibleCount += 8;
      this.renderScentLibrary();
    }

    /** The size (ml) currently chosen for this scent's card — defaults to the largest in-stock size, falling back to the first size if every size is out of stock. */
    getScentSize(scent) {
      if (!scent.sizes || !scent.sizes.length) return null;
      const chosenMl = this.state.scentSize[scent.id];
      const chosen = chosenMl != null ? scent.sizes.find((sz) => sz.size === chosenMl) : null;
      if (chosen && chosen.available) return chosen;
      return scent.sizes.find((sz) => sz.available) || scent.sizes[0];
    }

    selectScentSize(id, sizeMl) {
      const scent = this.scents.find((s) => s.id === id);
      if (!scent) return;
      const size = scent.sizes.find((sz) => sz.size === toNumber(sizeMl));
      if (!size || !size.available) return;
      this.state.scentSize[id] = size.size;
      this.renderScentLibrary();
    }

    /** Opens this card's size menu (closing any other open one). Clicking the same card's Select button again closes it. */
    toggleScentSizeMenu(id) {
      const wasOpen = Boolean(this.state.scentMenuOpen[id]);
      this.state.scentMenuOpen = {};
      this.state.scentMenuOpen[id] = !wasOpen;
      this.renderScentLibrary();
    }

    /** Picking a size from the menu sets it as chosen AND immediately adds that exact variant to the cart. */
    async pickScentSizeAndSelect(id, sizeMl) {
      const scent = this.scents.find((s) => s.id === id);
      if (!scent) return;
      const size = scent.sizes.find((sz) => sz.size === toNumber(sizeMl));
      if (!size || !size.available) return;
      this.state.scentSize[id] = size.size;
      this.state.scentMenuOpen[id] = false;
      await this.toggleScent(id);
    }

    /**
     * Selecting/unselecting a fragrance is now a UI-only state change — no
     * cart calls, nothing added or removed live. Which bottle sizes and
     * quantities this scent needs is worked out automatically in totals()
     * (yearly ml ÷ number of selected scents, then optimizeBottles per
     * scent) and only actually goes into the cart at checkout, in
     * addToCart().
     */
    toggleScent(id) {
      const scent = this.scents.find((s) => s.id === id);
      if (!scent) return;

      const idx = this.state.scents.indexOf(id);
      const wasSelected = idx !== -1;

      if (!wasSelected) {
        if (!scent.available) {
          return this.setScentMessage(esc(scent.title) + ' is currently out of stock.', true);
        }
        if (!scent.variantId) {
          return this.setScentMessage(esc(scent.title) + ' is not linked to a purchasable product yet.', true);
        }
        this.state.scents.push(id);
        this.setScentMessage(esc(scent.title) + ' added to your selection.', false);
      } else {
        this.state.scents.splice(idx, 1);
        this.setScentMessage(esc(scent.title) + ' removed from your selection.', false);
      }

      this.renderScentLibrary();
      this.renderConsumptionStep();
      this.renderFinalSummary();
      this.renderPricingStep();
      this.renderRoomMoods();
      this.renderMoodProfile();
      this.renderSummary();
    }

    setScentMessage(message, isError) {
      if (!this.el.scentMessage) return;
      this.el.scentMessage.textContent = message;
      this.el.scentMessage.classList.toggle('is-error', Boolean(isError));
    }

    /* --------------------------- 3.2 mood recommendation ---------------------------
       "What mood would you like to create?" — a primary + optional secondary
       mood, plus an optional per-room override. Matching is done for real:
       a mood's keyword list is checked (case-insensitive substring) against
       each in-stock scent's title + notes, never against fabricated names.
       Selecting a recommended scent reuses the exact same toggleScent()
       used by the manual scent library below, so it's the same selection
       state feeding pricing/cart everywhere else — mood is a shortcut into
       picking scents, not a separate parallel system.
       ---------------------------------------------------------------------- */

    renderMoodStep() {
      this.renderMoodPicker();
      this.renderRoomMoods();
      this.renderMoodProfile();
    }

    renderMoodPicker() {
      if (!this.el.moodGrid) return;
      this.el.moodGrid.innerHTML = MOOD_LIBRARY.map((m) => {
        const isPrimary = this.state.moodPrimary === m.key;
        const isSecondary = this.state.moodSecondary === m.key;
        const cls = 'aroma-mood-card' + (isPrimary ? ' is-primary' : '') + (isSecondary ? ' is-secondary' : '');
        return '<button type="button" class="' + cls + '" data-action="pick-mood" data-mood="' + esc(m.key) + '">' +
          '<span class="aroma-mood-card__emoji" aria-hidden="true">' + m.emoji + '</span>' +
          '<span class="aroma-mood-card__label">' + esc(m.label) + '</span>' +
          '<span class="aroma-mood-card__tagline">' + esc(m.tagline) + '</span>' +
          (isPrimary ? '<span class="aroma-mood-card__flag">Primary</span>' : '') +
          (isSecondary ? '<span class="aroma-mood-card__flag aroma-mood-card__flag--secondary">Secondary</span>' : '') +
        '</button>';
      }).join('');
    }

    pickMood(key) {
      const mood = MOOD_LIBRARY.find((m) => m.key === key);
      if (!mood) return;

      if (this.state.moodPrimary === key) {
        // Unselecting the primary promotes the secondary, if any.
        this.state.moodPrimary = this.state.moodSecondary;
        this.state.moodSecondary = null;
      } else if (this.state.moodSecondary === key) {
        this.state.moodSecondary = null;
      } else if (!this.state.moodPrimary) {
        this.state.moodPrimary = key;
      } else if (!this.state.moodSecondary) {
        this.state.moodSecondary = key;
      } else {
        // Both slots full: the new pick replaces the secondary.
        this.state.moodSecondary = key;
      }

      this.renderMoodStep();
    }

    /** Effective mood key(s) for one room — its own override, or the overall primary+secondary. */
    moodsForSpace(space) {
      const override = this.state.roomMoods[space.id];
      if (override) return [override];
      return [this.state.moodPrimary, this.state.moodSecondary].filter(Boolean);
    }

    pickRoomMood(spaceId, moodKey) {
      if (!spaceId || !moodKey) return;
      if (this.state.roomMoods[spaceId] === moodKey) {
        delete this.state.roomMoods[spaceId];
      } else {
        this.state.roomMoods[spaceId] = moodKey;
      }
      this.renderMoodStep();
    }

    clearRoomMood(spaceId) {
      if (!spaceId) return;
      delete this.state.roomMoods[spaceId];
      this.renderMoodStep();
    }

    /**
     * Scores in-stock scents against a set of mood keys by counting
     * keyword hits in the scent's title + notes (case-insensitive
     * substring match). Ties break on the scent's own star rating.
     * Returns [] when no mood is active or nothing in the live library
     * matches — never invents a result.
     */
    matchScentsForMoods(moodKeys, limit) {
      const max = limit || 4;
      if (!moodKeys || !moodKeys.length) return [];

      const keywords = [];
      moodKeys.forEach((key) => {
        const mood = MOOD_LIBRARY.find((m) => m.key === key);
        if (!mood) return;
        mood.keywords.forEach((kw) => { if (keywords.indexOf(kw) === -1) keywords.push(kw); });
      });

      return this.scents
        .filter((s) => s.available)
        .map((s) => {
          const haystack = (s.title + ' ' + (s.notes || '')).toLowerCase();
          const score = keywords.reduce((sum, kw) => sum + (haystack.indexOf(kw) !== -1 ? 1 : 0), 0);
          return { scent: s, score: score };
        })
        .filter((r) => r.score > 0)
        .sort((a, b) => (b.score - a.score) || (toNumber(b.scent.rating) - toNumber(a.scent.rating)))
        .slice(0, max);
    }

    renderRoomMoods() {
      if (!this.el.roomMoods) return;

      if (!this.state.spaces.length) {
        this.el.roomMoods.innerHTML = '<p class="aroma-empty">Add spaces in step 1 to set a mood per room.</p>';
        return;
      }

      this.el.roomMoods.innerHTML = this.state.spaces.map((space) => {
        const override = this.state.roomMoods[space.id];
        const effectiveKeys = this.moodsForSpace(space);
        const matches = this.matchScentsForMoods(effectiveKeys, 4);

        const chips = MOOD_LIBRARY.map((m) =>
          '<button type="button" class="aroma-room-mood__chip' + (override === m.key ? ' is-selected' : '') + '"' +
            ' data-action="pick-room-mood" data-id="' + esc(space.id) + '" data-mood="' + esc(m.key) + '">' +
            m.emoji + ' ' + esc(m.label) +
          '</button>'
        ).join('');

        const matchesHtml = matches.length
          ? matches.map((r) => {
              const isSelected = this.state.scents.indexOf(r.scent.id) !== -1;
              const stars = '★'.repeat(Math.max(0, Math.min(5, Math.round(toNumber(r.scent.rating) || 5))));
              return '<div class="aroma-mood-match">' +
                '<div class="aroma-mood-match__body">' +
                  '<span class="aroma-mood-match__name">' + esc(r.scent.title) + '</span>' +
                  '<span class="aroma-mood-match__stars" aria-hidden="true">' + stars + '</span>' +
                '</div>' +
                '<button type="button" class="aroma-mood-match__btn' + (isSelected ? ' is-selected' : '') + '"' +
                  ' data-action="toggle-scent" data-scent="' + esc(r.scent.id) + '"' +
                  ' aria-label="' + (isSelected ? 'Remove ' + esc(r.scent.title) + ' from your selection' : 'Add ' + esc(r.scent.title) + ' to your selection') + '">' +
                  (isSelected ? '✕ Remove' : 'Add') +
                '</button>' +
              '</div>';
            }).join('')
          : '<p class="aroma-mood-match__empty">' +
              (effectiveKeys.length ? 'No matching scents in the library yet for this mood.' : 'Pick a mood above to see recommendations for this room.') +
            '</p>';

        return '<div class="aroma-room-mood">' +
          '<div class="aroma-room-mood__head">' +
            '<span class="aroma-room-mood__label">' + esc(this.spaceLabel(space)) + '</span>' +
            (override ? '<button type="button" class="aroma-room-mood__clear" data-action="clear-room-mood" data-id="' + esc(space.id) + '">Use overall mood</button>' : '') +
          '</div>' +
          '<div class="aroma-room-mood__chips">' + chips + '</div>' +
          '<div class="aroma-mood-matches">' + matchesHtml + '</div>' +
        '</div>';
      }).join('');
    }

    /** "Your Personalized Aroma Profile" — room → mood → picked scent(s), plus the overall collection. */
    renderMoodProfile() {
      if (!this.el.moodProfile) return;

      const anyMood = Boolean(this.state.moodPrimary) || Object.keys(this.state.roomMoods).length > 0;
      if (!this.state.spaces.length || !anyMood) {
        this.el.moodProfile.innerHTML = '';
        return;
      }

      const rows = this.state.spaces.map((space) => {
        const effectiveKeys = this.moodsForSpace(space);
        if (!effectiveKeys.length) return null;

        const primaryKey = this.state.roomMoods[space.id] || this.state.moodPrimary;
        const mood = MOOD_LIBRARY.find((m) => m.key === primaryKey);
        const matches = this.matchScentsForMoods(effectiveKeys, 4);
        const pickedHere = matches.filter((r) => this.state.scents.indexOf(r.scent.id) !== -1);
        const shown = pickedHere.length ? pickedHere : matches.slice(0, 1);
        const scentLabel = shown.length ? shown.map((r) => r.scent.title).join(' & ') : '—';

        return '<div class="aroma-mood-profile__row">' +
          '<span class="aroma-mood-profile__space">' + esc(this.spaceLabel(space)) + '</span>' +
          '<span class="aroma-mood-profile__mood">' + (mood ? mood.emoji + ' ' + esc(mood.label) : '—') + '</span>' +
          '<span class="aroma-mood-profile__scent">' + esc(scentLabel) + '</span>' +
        '</div>';
      }).filter(Boolean).join('');

      if (!rows) {
        this.el.moodProfile.innerHTML = '';
        return;
      }

      const collection = this.state.scents.map((id) => this.scents.find((s) => s.id === id)).filter(Boolean);
      const collectionHtml = collection.length
        ? collection.map((s) => '<span class="aroma-mood-profile__chip">' + esc(s.title) + '</span>').join('')
        : '<span class="aroma-mood-profile__empty">No scents selected yet</span>';

      this.el.moodProfile.innerHTML =
        '<h3 class="aroma-mood-profile__title">Your Personalized Aroma Profile</h3>' +
        '<div class="aroma-mood-profile__rows">' + rows + '</div>' +
        '<h4 class="aroma-mood-profile__collection-title">Your Scent Collection</h4>' +
        '<div class="aroma-mood-profile__collection">' + collectionHtml + '</div>';
    }

    scentCategories() {
      const set = new Set();
      this.scents.filter((s) => s.available).forEach((s) => s.categories.forEach((c) => set.add(c)));
      return Array.from(set);
    }

    filteredScents() {
      const query = (this.state.scentSearch || '').trim().toLowerCase();
      return this.scents.filter((s) => {
        if (!s.available) return false;
        if (this.state.scentCategory && s.categories.indexOf(this.state.scentCategory) === -1) return false;
        if (query && s.title.toLowerCase().indexOf(query) === -1) return false;
        return true;
      });
    }

    renderScentLibrary() {
      if (!this.el.scentGrid) return;

      const cats = this.scentCategories();
      if (this.el.scentCats) {
        this.el.scentCats.innerHTML = cats.map((cat) =>
          '<button type="button" class="aroma-scent-chip' + (this.state.scentCategory === cat ? ' is-selected' : '') + '" data-scent-cat="' + esc(cat) + '">' + esc(cat) + '</button>'
        ).join('');
      }

      if (!this.scents.length) {
        this.el.scentGrid.innerHTML = '<p class="aroma-empty">The signature scent library has not been connected to any products yet.</p>';
        if (this.el.scentCollection) this.el.scentCollection.innerHTML = '';
        return;
      }

      const list = this.filteredScents();
      const visible = list.slice(0, this.state.scentVisibleCount);

      this.el.scentGrid.innerHTML = visible.length
        ? visible.map((s) => {
            const selected = this.state.scents.indexOf(s.id) !== -1;
            const stars = '★★★★★'.slice(0, Math.round(s.rating)) + '☆☆☆☆☆'.slice(0, 5 - Math.round(s.rating));
            const chosenSize = this.getScentSize(s);
            const menuOpen = Boolean(this.state.scentMenuOpen[s.id]);

            // Simple select / unselect: no size menu. Selecting a card adds
            // the scent's in-stock variant (see getScentSize — largest
            // available size) straight to the cart in one step; selecting
            // it again removes it.
            const sizeMenu = '';

            const selectBtn = selected
              ? '<button type="button" class="aroma-btn aroma-scent-card__btn is-selected" data-action="toggle-scent" data-scent="' + esc(s.id) + '">Remove ✕</button>'
              : '<button type="button" class="aroma-btn aroma-scent-card__btn" data-action="toggle-scent" data-scent="' + esc(s.id) + '">Select</button>';

            return '<div class="aroma-scent-card' + (selected ? ' is-selected' : '') + '">' +
              '<div class="aroma-scent-card__imgwrap">' +
                (selected ? '<button type="button" class="aroma-scent-card__badge" data-action="toggle-scent" data-scent="' + esc(s.id) + '" title="Remove from your collection" aria-label="Remove ' + esc(s.title) + '">✓</button>' : '') +
                (s.image ? '<img class="aroma-scent-card__img" src="' + esc(s.image) + '" alt="' + esc(s.title) + '" loading="lazy" onerror="this.replaceWith(Object.assign(document.createElement(\'span\'),{className:\'aroma-scent-card__noimg\',textContent:\'✦\'}))">' : '<span class="aroma-scent-card__noimg">✦</span>') +
              '</div>' +
              '<div class="aroma-scent-card__body">' +
                '<div class="aroma-scent-card__name">' + esc(s.title) + '</div>' +
                '<div class="aroma-scent-card__meta">' +
                  '<span class="aroma-scent-card__stars" aria-hidden="true">' + stars + '</span>' +
                  (Number.isFinite(chosenSize && chosenSize.price) ? '<span class="aroma-scent-card__price">' + this.money(chosenSize.price) + '</span>' : (Number.isFinite(s.price) ? '<span class="aroma-scent-card__price">' + this.money(s.price) + '</span>' : '')) +
                '</div>' +
                (s.notes ? '<div class="aroma-scent-card__notes">' + esc(s.notes) + '</div>' : '') +
                selectBtn +
                sizeMenu +
              '</div>' +
            '</div>';
          }).join('')
        : '<p class="aroma-empty">No fragrances match that search.</p>';

      if (this.el.scentShowMoreWrap) {
        this.el.scentShowMoreWrap.innerHTML = list.length > visible.length
          ? '<button type="button" class="aroma-btn aroma-btn--ghost aroma-scent-showmore" data-action="show-more-scents">Show more (' + (list.length - visible.length) + ' left)</button>'
          : '';
      }

      if (this.el.scentCollection) {
        const chosen = this.scents.filter((s) => this.state.scents.indexOf(s.id) !== -1);
        this.el.scentCollection.innerHTML = chosen.length
          ? '<p class="aroma-scent-collection__list">' +
              chosen.map((s) => '<span class="aroma-scent-collection__item">✔ ' + esc(s.title) + '</span>').join('') +
            '</p><p class="aroma-scent-collection__count">' + chosen.length + ' fragrance' + (chosen.length === 1 ? '' : 's') + ' selected</p>'
          : '<p class="aroma-empty">Select at least one fragrance to build your collection.</p>';
      }
    }

    /* --------------------------- 3.5 final summary --------------------------- */
    renderFinalSummary() {
      if (!this.el.finalSummary) return;
      const t = this.totals();
      const config = PROPERTY_TYPES[this.state.propertyType];

      // Product names, not a bare unit count — e.g. "SCENT SPLASH Diffuser ×2".
      const diffuserNames = {};
      t.plan.forEach((row) => {
        if (!row.diffuser || !row.units) return;
        const key = row.diffuser.id;
        if (!diffuserNames[key]) diffuserNames[key] = { title: row.diffuser.title, qty: 0 };
        diffuserNames[key].qty += row.units;
      });
      const diffuserLine = Object.keys(diffuserNames).length
        ? Object.keys(diffuserNames).map((key) => diffuserNames[key].title + ' ×' + diffuserNames[key].qty).join(', ')
        : '—';

      const hasFragrance = Boolean(t.selectedScents.length);

      const scentNamesLine = hasFragrance
        ? t.selectedScents.map((s) => s.title).join(', ')
        : '—';

      // t.fragranceEstimate reflects only the signature scent(s) the
      // customer has manually picked — see totals() for the calculation.

      // Oil consumption is per-diffuser and often a published range (e.g.
      // 0.2–1.0 ml/hr), not one number — never averaged. If every diffuser
      // actually used in the plan shares the same rate, show it labelled
      // exactly against the min and max totals it produces. If the plan
      // mixes different diffuser models with different rates, show the
      // combined min–max total instead of a single rate label that
      // wouldn't truthfully apply to all of them.
      const usedDiffusers = t.plan.filter((row) => row.diffuser && row.units).map((row) => row.diffuser);
      const uniqueRateKeys = [];
      usedDiffusers.forEach((d) => {
        const key = d.mlPerHourMin + '|' + d.mlPerHourMax;
        if (uniqueRateKeys.indexOf(key) === -1) uniqueRateKeys.push(key);
      });

      let consumptionHtml;
      if (!usedDiffusers.length) {
        consumptionHtml = esc('—');
      } else if (uniqueRateKeys.length === 1 && t.hasConsumptionRange) {
        const d = usedDiffusers[0];
        consumptionHtml =
          '<ul class="aroma-final-summary__range">' +
            '<li>(' + esc(String(d.mlPerHourMin)) + ' ml/hour): ' + Math.round(t.totalYearlyOilMlMin).toLocaleString('en-US') + ' ml/year</li>' +
            '<li>(' + esc(String(d.mlPerHourMax)) + ' ml/hour): ' + Math.round(t.totalYearlyOilMl).toLocaleString('en-US') + ' ml/year</li>' +
          '</ul>';
      } else if (uniqueRateKeys.length === 1) {
        consumptionHtml = esc(Math.round(t.totalYearlyOilMl).toLocaleString('en-US') + ' ml/year');
      } else {
        consumptionHtml = esc(
          (t.hasConsumptionRange
            ? Math.round(t.totalYearlyOilMlMin).toLocaleString('en-US') + '–' + Math.round(t.totalYearlyOilMl).toLocaleString('en-US')
            : Math.round(t.totalYearlyOilMl).toLocaleString('en-US')) + ' ml/year'
        );
      }

      const rows = [
        ['Property type', config ? (this.state.propertyName || config.label) : '—'],
        ['Total area', t.area.toLocaleString('en-US') + ' sqft'],
        ['Recommended diffusers', diffuserLine],
        ['Daily usage', t.hoursPerDay + ' hours'],
        ['Operating days', t.daysPerMonth + ' days'],
        ['Estimated monthly runtime', Math.round(t.monthlyRuntimeHours).toLocaleString('en-US') + ' hours'],
        ['Estimated annual runtime', Math.round(t.annualRuntimeHours).toLocaleString('en-US') + ' hours'],
        ['Estimated yearly consumption', consumptionHtml, true],
        ['Selected signature scents', scentNamesLine],
        ['Number of fragrance bottles', t.fragranceEstimate.label],
        ['Estimated fragrance cost', t.fragranceEstimate.totalCost > 0 ? this.money(t.fragranceEstimate.totalCost) : '—']
      ];

      this.el.finalSummary.innerHTML = rows.map((row) =>
        '<div class="aroma-final-summary__row"><dt>' + esc(row[0]) + '</dt><dd>' + (row[2] ? row[1] : esc(row[1])) + '</dd></div>'
      ).join('');
    }

    renderPricingStep() {
      const t = this.totals();
      const commercial = this.isCommercial();

      this.el.onceHeadline.innerHTML = this.money(t.onceTotal) + ' <small>first-year cost</small>';
      this.el.membershipHeadline.innerHTML = this.money(t.membershipTotal) + ' <small>first-year cost</small>';

      const rows = [
        ['Diffusers', this.money(t.equipment), this.money(t.equipmentMemberPrice)],
        ['Fragrance bottles', this.money(t.fragranceCost), this.money(t.fragranceMemberCost)]
      ];

      this.el.compareBody.innerHTML = rows.map((row) =>
        '<tr>' +
          '<td>' + esc(row[0]) + '</td>' +
          '<td class="aroma-table__num">' + esc(row[1]) + '</td>' +
          '<td class="aroma-table__num">' + esc(row[2]) + '</td>' +
        '</tr>'
      ).join('') +
      '<tr class="is-total">' +
        '<td>Total first-year cost</td>' +
        '<td class="aroma-table__num">' + this.money(t.onceTotal) + '</td>' +
        '<td class="aroma-table__num">' + this.money(t.membershipTotal) + '</td>' +
      '</tr>' +
      '<tr class="is-savings">' +
        '<td>Estimated savings</td>' +
        '<td class="aroma-table__num">—</td>' +
        '<td class="aroma-table__num">' + (t.savings > 0 ? this.money(t.savings) : '—') + '</td>' +
      '</tr>';

      if (t.savings > 0) {
        this.el.savings.textContent = 'Membership saves ' + this.money(t.savings) + ' in the first year.';
      } else if (t.savings < 0) {
        this.el.savings.textContent = 'Buying once saves ' + this.money(Math.abs(t.savings)) + ' in the first year. Members still get ' + t.diffuserDiscountPercent + '% off diffusers and ' + t.scentDiscountPercent + '% off fragrance bottles.';
      } else {
        this.el.savings.textContent = '';
      }

      this.el.checkoutHint.textContent = commercial
        ? this.settings.quoteText + ' You can still add equipment to your cart to reserve it.'
        : 'Everything in your plan goes into the cart in one step.';

      this.el.plans.forEach((card) => {
        const on = card.dataset.plan === this.state.plan;
        card.classList.toggle('is-selected', on);
        card.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    renderSummary() {
      const t = this.totals();
      const config = PROPERTY_TYPES[this.state.propertyType];

      const set = (key, value) => {
        const node = this.root.querySelector('[data-summary="' + key + '"]');
        if (!node || node.textContent === value) return;
        node.textContent = value;
        node.classList.add('is-bumped');
        window.setTimeout(() => node.classList.remove('is-bumped'), 450);
      };

      set('property', config ? (this.state.propertyName || config.label) : '—');
      set('floors', config && config.floors ? String(this.state.floors) : '—');
      set('spaces', String(this.state.spaces.reduce((sum, s) => sum + (parseInt(s.qty, 10) || 1), 0)));
      set('area', t.area.toLocaleString('en-US') + ' sqft');
      set('diffusers', String(t.units));
      set('coverage', t.coverage.toLocaleString('en-US') + ' sqft');
      set('equipment', t.equipment ? this.money(t.equipmentForPlan) : '—');
      set('fragranceestimate', t.fragranceEstimate.totalCost > 0 ? this.money(t.fragranceEstimateForPlan) : '—');

      if (this.el.summaryScents && this.el.summaryScentsList) {
        if (t.fragranceBreakdown.length) {
          this.el.summaryScents.hidden = false;
          this.el.summaryScentsList.innerHTML = t.fragranceBreakdown.map((f) => {
            // Label the size alongside the name — the same real,
            // in-stock variant referencePrice was priced from (Shopify's
            // selected_or_first_available_variant), so name/size/price
            // always describe the exact same bottle.
            const defaultSize = (f.scent.sizes || []).find((sz) => sz.variantId === f.scent.variantId);
            const sizeLabel = defaultSize && defaultSize.size ? ' (' + defaultSize.size + 'ML)' : '';
            return '<li><span>' + esc(f.scent.title) + esc(sizeLabel) + '</span><span>' + this.money(f.referencePrice) + '</span></li>';
          }).join('');
        } else {
          this.el.summaryScents.hidden = true;
          this.el.summaryScentsList.innerHTML = '';
        }
      }
      set('grandtotal', t.grandTotal ? this.money(t.grandTotal) : '—');

      const note = this.root.querySelector('[data-summary="note"]');
      if (note) {
        note.textContent = t.units
          ? 'Coverage exceeds your floor area by ' + Math.max(0, t.coverage - t.area).toLocaleString('en-US') + ' sqft, which keeps scent even at peak hours.'
          : 'Add spaces to see your plan build in real time.';
      }

      if (this.el.summaryMobile) {
        const spaceCount = this.state.spaces.length;
        this.el.summaryMobile.textContent = spaceCount + (spaceCount === 1 ? ' space · ' : ' spaces · ') + t.area.toLocaleString('en-US') + ' sqft';
      }
    }

    isCommercial() {
      const config = PROPERTY_TYPES[this.state.propertyType];
      return Boolean(config && config.commercial);
    }

    toggleSummary(trigger) {
      if (!this.el.aside) return;
      const open = this.el.aside.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    selectPlan(plan) {
      this.state.plan = plan;
      this.el.plans.forEach((card) => {
        const on = card.dataset.plan === plan;
        card.classList.toggle('is-selected', on);
        card.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      // The sidebar (Equipment/Fragrance/Total estimate) is plan-aware now,
      // so it must be re-rendered whenever the plan changes, otherwise it
      // keeps showing whichever figure was last drawn.
      this.renderSummary();
    }

    /* ----------------------------- diffuser picker ----------------------------- */
    openPicker(spaceId) {
      const space = this.state.spaces.find((s) => s.id === spaceId);
      if (!space) return;
      this.state.editingSpaceId = spaceId;
      const current = this.matchDiffuser(space);

      const pool = this.eligibleDiffusers();

      this.el.modalBody.innerHTML = pool.map((d) => {
        const area = toNumber(space.area);
        const fits = area > 0 && area <= d.coverageMax;
        const needed = this.unitsForRoom(area, d);
        return '<button type="button" class="aroma-option' + (current && current.id === d.id ? ' is-current' : '') + '" data-action="pick-diffuser" data-diffuser="' + esc(d.id) + '">' +
          (d.image ? '<img class="aroma-unit__img" src="' + esc(d.image) + '" alt="" loading="lazy" onerror="this.style.display=\'none\'">' : '') +
          '<span class="aroma-option__body">' +
            '<span class="aroma-option__name">' + esc(d.title) + '</span>' +
            '<span class="aroma-option__meta">Covers up to ' + d.coverageMax.toLocaleString('en-US') + ' sqft' + (d.sku ? ' · ' + esc(d.sku) : '') + '</span>' +
            (needed > 1 ? '<span class="aroma-option__meta">' + needed + ' units needed for this room</span>' : '') +
            (fits && current && current.id === d.id ? '<span class="aroma-option__fit">Recommended fit</span>' : '') +
            (!d.available ? '<span class="aroma-option__fit aroma-option__fit--out">Out of stock</span>' : '') +
          '</span>' +
          '<span class="aroma-option__price">' + this.money(d.price) + '</span>' +
        '</button>';
      }).join('');

      this.el.modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    /** Select one of the inline suggestions on a plan row. */
    chooseForSpace(spaceId, diffuserId) {
      if (!spaceId || !diffuserId) return;
      if (diffuserId === 'combo-splash-sensei') {
        if (!this.comboFromPool()) return;
        this.state.overrides[spaceId] = diffuserId;
        this.renderPlanStep();
        this.renderSummary();
        return;
      }
      const diffuser = this.diffusers.find((d) => d.id === diffuserId);
      if (!diffuser || !diffuser.available) return;
      this.state.overrides[spaceId] = diffuserId;
      this.renderPlanStep();
      this.renderSummary();
    }

    pickDiffuser(diffuserId) {
      const diffuser = this.diffusers.find((d) => d.id === diffuserId);
      if (!diffuser || !diffuser.available) return;
      if (this.state.editingSpaceId) {
        this.state.overrides[this.state.editingSpaceId] = diffuserId;
      }
      this.closePicker();
      this.renderPlanStep();
      this.renderSummary();
    }

    closePicker() {
      this.el.modal.hidden = true;
      this.state.editingSpaceId = null;
      document.body.style.overflow = '';
    }

    /* --------------------------------- cart --------------------------------- */
    lineProperties() {
      const config = PROPERTY_TYPES[this.state.propertyType];
      return {
        'Property': config ? config.label : '',
        'Property name': this.state.propertyName || '—',
        'Floors': String(this.state.floors),
        'HVAC': this.state.hvac || 'Not specified',
        'Contact': this.state.customer.name,
        'Phone': this.state.customer.phoneNormalized || this.state.customer.phone,
        'Email': this.state.customer.email,
        'Notes': this.state.customer.notes || '—'
      };
    }

    async addToCart(mode) {
      const t = this.totals();

      if (!t.units) {
        return this.setCartMessage('Add at least one space with an area before adding to cart.', true);
      }

      const grouped = {};
      t.plan.forEach((row) => {
        if (!row.diffuser || !row.units) return;
        // A combo row (Scent Splash + Sensei) isn't a real Shopify variant
        // on its own — expand it into its two actual products so each
        // gets its own real line in the cart. Every normal row is just
        // itself, wrapped as a single "part".
        const parts = row.diffuser.isCombo ? row.diffuser.parts : [{ diffuser: row.diffuser, units: 1 }];
        parts.forEach((part) => {
          if (!part.diffuser || !part.diffuser.variantId) return;
          const key = part.diffuser.variantId;
          const qty = part.units * row.units;
          if (!grouped[key]) grouped[key] = { diffuser: part.diffuser, qty: 0, rooms: [] };
          grouped[key].qty += qty;
          grouped[key].rooms.push(row.label + (qty > 1 ? ' ×' + qty : ''));
        });
      });

      const items = Object.keys(grouped).map((variantId) => ({
        id: String(variantId),
        quantity: grouped[variantId].qty,
        properties: Object.assign({}, this.lineProperties(), {
          'Plan': mode === 'membership' ? 'Membership' : 'Buy once',
          'Placed in': grouped[variantId].rooms.join(', ')
        })
      }));

      // Fragrance bottles are never added to the cart at selection time —
      // the customer only picks which oil(s) they want, and the required
      // bottle sizes/quantities are worked out automatically from their
      // yearly consumption (t.fragranceBreakdown, from totals()). Those
      // bottles are added here, at checkout, one cart line per bottle
      // size actually needed for each selected fragrance.
      t.fragranceBreakdown.forEach((f) => {
        f.bottlePlan.lines.forEach((line) => {
          if (!line.qty) return;
          const sizeMatch = f.scent.sizes.find((sz) => sz.size === line.size);
          const variantId = sizeMatch && sizeMatch.variantId;
          if (!variantId) return;
          items.push({
            id: String(variantId),
            quantity: line.qty,
            properties: Object.assign({}, this.lineProperties(), {
              'Plan': mode === 'membership' ? 'Membership' : 'Buy once',
              'Selected in': 'Signature scent library',
              'Fragrance': f.scent.title
            })
          });
        });
      });

      if (mode === 'membership') {
        const membership = this.settings.membership || {};
        if (membership.variantId) {
          items.unshift({
            id: String(membership.variantId),
            quantity: t.units,
            properties: Object.assign({}, this.lineProperties(), {
              'Plan': 'Membership',
              'Monthly': this.money(t.membershipMonthly),
              'Diffusers': String(t.units)
            })
          });
        }
      }

      if (!items.length) {
        return this.setCartMessage(
          'These diffusers are not linked to products yet. Connect a product reference on each diffuser metaobject, then try again.',
          true
        );
      }

      this.setCartMessage('Adding your plan to the cart…', false);
      this.toggleCartButtons(true);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'The cart rejected this request.');
        }

        this.setCartMessage('Added. Taking you to the cart…', false);
        window.location.href = this.settings.cartUrl || '/cart';
      } catch (error) {
        this.toggleCartButtons(false);
        this.setCartMessage(error.message, true);
      }
    }

    toggleCartButtons(disabled) {
      Array.from(this.root.querySelectorAll('[data-action="add-once"], [data-action="add-membership"]'))
        .forEach((btn) => { btn.disabled = disabled; });
    }

    setCartMessage(message, isError) {
      if (!this.el.cartMessage) return false;
      this.el.cartMessage.textContent = message;
      this.el.cartMessage.classList.toggle('is-error', Boolean(isError));
      return false;
    }
  }

  /* ------------------------------- bootstrap ------------------------------- */
  const boot = () => {
    injectMobileNavStyles();
    Array.from(document.querySelectorAll('[data-aroma-root]')).forEach((root) => {
      if (root.dataset.aromaReady === 'true') return;
      root.dataset.aromaReady = 'true';
      new AromaConfigurator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor support: re-initialise when the section is reloaded.
  document.addEventListener('shopify:section:load', boot);
})();